import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { COMMANDS } from '../../../utils/commonProperties';
import { createSingleSocket, singleCircleBuat, singleSquareBuat } from '../../../factory/symbols/createSocketSymbols';
import { SocketCommand } from '../../../modules/undoRedo';
import { useBaseWiring } from './useBaseWiring';
import { useWireRotateOffset } from './useWireRotateOffset';
import { useWireKeyboardMovement } from './useWireKeyboardMovement';
import useFloorTransitionArrow from './useFloorTransitionArrow';
import { useWireLocalUndoRedo } from './useWireLocalUndoRedo';
import { useWireAutoSelectBuat } from './useWireAutoSelectBuat';
import { useSelector } from 'react-redux';
import parseColor from '../../../utils/parseColor';
import { getSnappedPoint, getSnappedPointForPanel } from '../../../shared/snap/getSnappedPoint';
import { applyGridSnap, applyRelativeGridSnap, getOrthoSnappedPoint } from '../../../modules/drawing';
import { calculatePerpendicularExitPoint } from '../utils/calculatePerpendicularExit';
import { handleSnapWall } from '../utils/snapWallHandler';
import { getMidpointSnaps } from '../utils/getMidpointSnaps';
import { WireMarkerGenerator } from '../utils/WireMarkerGenerator';
import { createSnapMarker } from '../../../shared/snap/createSnapMarker';
import { getPerpendicularIntersection, isAxisAligned, getPreviewRayIntersection, getLineDirection, getDirectionalIntersection } from '../../../shared/geometry/getPrependicularIntersection';
import { resetOperation, setCommandMessage } from '../../../redux/operationSlice';
import { addWire } from '../../../redux/wireSlice';
import { selectAllFloorPlans, selectSortedFloorPlans, selectActiveFloorPlanId } from '../../../redux/floorPlanSlice';
import { addBulk } from '../../../redux/simpleSupplySlice';
import { addActions } from '../../../redux/operationHistorySlice';
import { updatePanelTitles, updatePanelInputWire, updatePanelWiringPositions, updatePanelExitPoints, calculatePanelExitCount, findPanelExitPoints } from '../utils/panelExitManager';
import { ComponentFactory } from '../../../factories/ComponentFactory';
import { FeederFactory } from '../../../factories/FeederFactory';
import { generateUniqueId } from '../../../utils/generateUniqueId';
import { getObjectById } from '../../../utils/getObjectById';
import { createArrow, createIncomingArrow } from '../../../factory/symbols/createArrows';
import { FLOOR_ARROW_ROTATION, updateFloorArrowPreview, placeCrossFloorMarker } from '../utils/floorTransition';
import { findNearestCableShaft } from '../../../utils/cableShaftUtils';
import { getPointOnDiagonal } from '../utils/cableShaftExitManager';
import { findGuideWireAtPoint, projectOntoWire, getSideOfWire, computeOffsetTracePath } from '../utils/wireTraceUtils';
import SceneCleanupManager from '../../../utils/scene/SceneCleanupManager';
import { dedupAllWiresInScene } from '../utils/dedupWireVertices';

// Priz/aydınlatma hattı YALNIZ kendi tipindeki hat/buat/pano'dan (veya kablo
// bacasından) başlatılabilir. Duvar ya da başka-tip çizgi köşesinden başlatılan hat
// bir kaynağa bağlanamaz → tamamlanınca orphan olup siliniyordu (wire kayboluyor,
// prizler kalıyor). Bu yardımcı, ilk-tıklama snap kaynağını doğrular.
function isValidWireStartSource(snapSource, commandType) {
  if (!snapSource) return false;
  const t = snapSource.type;
  const parent = snapSource.parent;
  // Pano çıkışı / kablo bacası her tip için geçerli başlangıç.
  if (t === 'panelConnection' || t === 'cableShaftConnection') return true;
  if (commandType === COMMANDS.DRAW_SOCKET) {
    if (t === 'socketBuat') return true;
    if (t === 'vertex' || t === 'start' || t === 'end') {
      return !!(parent && (parent.function === 'simpleSocket' || parent.socketLoadId != null));
    }
    return false;
  }
  if (commandType === COMMANDS.DRAW_LIGHT) {
    if (t === 'lightingBuat') return true;
    if (t === 'vertex' || t === 'start' || t === 'end') {
      return !!(parent && (parent.function === 'simpleLighting' || parent.lightingLoadId != null));
    }
    return false;
  }
  return true; // diğer wire tipleri (mainPanel, electricalPanel, bell...) kısıtlanmaz
}

const useComponentWiring = (scene, camera, renderer, snapPoints, options = {}) => {
    // 🟢 DEBUG: Check options passed to useComponentWiring
     
    const socketTypes = [
        { name: 'Normal Priz', type: 'normal', power: 300 },
        { name: 'kapaklı', type: 'kapaklı', power: 300 },
        { name: 'etanj', type: 'etanj', power: 300 },
    ];

    // read current settings to seed default wire/symbol colors into the wiring config
    const settings = useSelector(state => state.settings);
    const floorPlans = useSelector(selectAllFloorPlans);
    const sortedFloors = useSelector(selectSortedFloorPlans);
    const activeFloorPlanId = useSelector(selectActiveFloorPlanId);
    // parse configured wire color so base.wireColor is numeric (avoid passing 8-digit hex strings)
    const parsedWireColor = parseColor(settings?.socket?.wireColor ?? 0x0045ff);

    // local rotate offset (degrees): H5'te useWireRotateOffset hook'una çıkarıldı
    // (base oluştuktan sonra çağırılır — aşağıda)

    // ✅ Track last restored ID to prevent infinite loop
    const lastRestoredIdRef = useRef(null);

    // ✅ Ok sembolü ön izleme için ref (toggle mantığı)
    const arrowPreviewRef = useRef(null);        // Ön izleme ok sembolü
    const arrowPreviewActiveRef = useRef(false); // Ön izleme aktif mi?
    const arrowPreviewRotationRef = useRef(60);  // Ön izleme açısı

    // ✅ Kablo bacası kontrolü için ref'ler
    const isInsideCableShaftRef = useRef(false); // Mouse kablo bacası içinde mi?
    const currentCableShaftRef = useRef(null);   // Mevcut kablo bacası objesi
    const selectedExitPointRef = useRef(null);   // ✅ Seçili çıkış noktası
    const exitPointSnapMarkerRef = useRef(null); // ✅ Çıkış noktası snap marker

    // ✅ Kablo bacasından çıkış (devam) modu için ref'ler
    const continueFromShaftModeRef = useRef(false); // Kablo bacasından devam modu aktif mi?
    const continueFromShaftDataRef = useRef(null);  // Devam edilecek kablo bilgisi

    // Trace follow (Ctrl+Shift) — mevcut bir hattı 5 birim ofsetle takip eder
    const shiftPressedRef     = useRef(false);
    const traceGuideRef       = useRef(null);  // { wire, positions, projT }
    const traceGuideSideRef   = useRef(0);     // 0=belirsiz, +1/-1
    const tracePreviewPtsRef  = useRef([]);

const base = useBaseWiring({
  scene,
  camera,
  renderer,
  snapPoints,
  options,
  config: {
    commandType: options.commandType,
    wireColor: options.wireColor,
    itemTypes: options.itemTypes,
    itemTypeName: options.itemTypeName,
    createSymbol: options.createSymbol,
    createCommand: options.createCommand,
    // ✅ BellButton, Machine, Bell için gerekli ek özellikler
    createFeederData: options.createFeederData,
    createComponentData: options.createComponentData,
    skipItemExecution: options.skipItemExecution,
    allowSpaceKey: options.allowSpaceKey,
    itemOffset: options.itemOffset,
    // ✅ Fixture için spline desteği
    // NOT: isRedo/isRestoring sadece vertex simplification'ı etkiler, spline modunu DEĞİL
    // Spline modu sadece options.simplifyCollinearVertices === false ise aktif olur (fixture için)
    simplifyCollinearVertices: options.simplifyCollinearVertices,
    // ✅ MainPanel için dik çıkış devre dışı
    disablePerpendicularExit: options.disablePerpendicularExit,
    // ✅ Wire marker sayısı (varsayılan 3, aydınlatma için 2)
    markerCount: options.markerCount
  }
});

    // H5-1: rotate offset (R tuşu) ayrı hook'ta — rotateOffsetRef hala base ile aynı
    // pattern'de okunur (yerleştirme + preview satırlarında base.X gibi)
    const { rotateOffsetRef } = useWireRotateOffset({ scene, base, options });

    // H5-2: AWSD klavye hareket (snap'ten küçük offset) ayrı hook'ta
    useWireKeyboardMovement({ base });

    // Kat-geçiş oku — preview ok'unu mouse'a takip ettir (baca dışında). Ok keydown'da
    // floorTransition.updateFloorArrowPreview ile oluşuyor; bu hook rAF ile takip ettirir.
    useFloorTransitionArrow({ scene, base, arrowPreviewRef, arrowPreviewActiveRef, isInsideCableShaftRef });

    // ✅ Wire placement'ı tamamla (hem Enter'da hem de son vertex eklendiğinde)
    const confirmPlacement = () => {
        // ✅ Ok ön izlemesi aktifse: Sadece ok sembolü ekle, komponent ekleme, Redux işlemi yapma
        if (arrowPreviewActiveRef.current && arrowPreviewRef.current) {
            // Ok sembolünü kalıcı hale getir (preview'dan çıkar, kalıcı olarak ekle)
            const arrowPosition = base.previewEndPointRef.current?.clone() || 
                                  base.verticesRef.current[base.verticesRef.current.length - 1]?.clone();
            
            if (arrowPosition) {
                // Preview ok'u kaldır
                scene.remove(arrowPreviewRef.current);
                arrowPreviewRef.current.traverse(child => {
                    if (child.geometry) child.geometry.dispose();
                    if (child.material) child.material.dispose();
                });
                
                // Kalıcı ok sembolü oluştur (wire rengi ile)
                const permanentArrow = createArrow(scene, {
                    position: { x: arrowPosition.x, y: arrowPosition.y },
                    rotation: arrowPreviewRotationRef.current,
                    color: base.wireColor || 0x00ff00,
                    layerName: '0',
                    isPreview: false
                });
                
                // Ok sembolüne userData ekle (tip bilgisi için)
                permanentArrow.userData = {
                    type: 'floorTransitionArrow',
                    direction: arrowPreviewRotationRef.current === 60 ? 'up' : 
                              arrowPreviewRotationRef.current === 240 ? 'down' :
                              arrowPreviewRotationRef.current === 180 ? 'left' : 'right',
                    rotation: arrowPreviewRotationRef.current
                };
                
                arrowPreviewRef.current = null;
                arrowPreviewActiveRef.current = false;

                // ✅ Ok pozisyonunu vertices'e ekle (son kenar için)
                base.verticesRef.current.push(arrowPosition);

                // Wire'ı oluştur ama komponent/supply ekleme
                if (base.verticesRef.current.length > 1) {
                    // Wire ID oluştur
                    const wireId = generateUniqueId();

                    // Kablo bacası bağlantı bilgisini hazırla
                    const shaftData = currentCableShaftRef.current?.userData;

                    // ✅ Çıkış noktası sistemi - boş nokta bul veya yeni ekle
                    let exitPointPosition = { x: arrowPosition.x, y: arrowPosition.y };
                    let exitIndex = -1;

                    if (shaftData && shaftData.diagonalStart && shaftData.diagonalEnd) {
                        const shaftId = shaftData.id;
                        const shaftGroup = currentCableShaftRef.current;

                        // ✅ Önce seçili çıkış noktası var mı kontrol et (kullanıcı snap ile seçtiyse)
                        const selectedPoint = selectedExitPointRef.current;

                        // Mevcut çıkış noktalarını kontrol et
                        const existingExitPoints = [];
                        shaftGroup.traverse((child) => {
                            if (child.userData?.type === 'shaftExitPoint') {
                                existingExitPoints.push(child);
                            }
                        });
                        existingExitPoints.sort((a, b) => a.userData.exitIndex - b.userData.exitIndex);

                        // Seçili nokta varsa onu kullan, yoksa boş noktayı bul
                        const targetPoint = selectedPoint || existingExitPoints.find(p => !p.userData.isOccupied);

                        if (targetPoint) {
                            // Hedef nokta var, kullan
                            exitPointPosition = { x: targetPoint.position.x, y: targetPoint.position.y };
                            exitIndex = targetPoint.userData.exitIndex;

                            // Noktayı dolu olarak işaretle
                            targetPoint.userData.isOccupied = true;
                            targetPoint.userData.wireId = wireId;
                            targetPoint.userData.direction = 'in';
                            targetPoint.material.color.setHex(0xff6600);

                            // ✅ occupiedExitCount'u artır
                            shaftData.occupiedExitCount = (shaftData.occupiedExitCount || 0) + 1;

                            // Seçili noktayı temizle
                            selectedExitPointRef.current = null;
                        } else {
                            // Yeni çıkış noktası gerekli (bu durum normalde olmamalı, önizleme sırasında oluşturulmalı)
                        }

                        // Wire'ın son vertex'ini çıkış noktasına taşı ve b vertex'ini K kenarının yönünü koruyarak ötele
                        if (base.verticesRef.current.length >= 2) {
                            const vertices = base.verticesRef.current;
                            const cIdx = vertices.length - 1; // Son vertex (c)
                            const bIdx = vertices.length - 2; // Bir önceki vertex (b)

                            const cX = exitPointPosition.x;
                            const cY = exitPointPosition.y;
                            const bOld = vertices[bIdx];

                            let newBx, newBy;

                            if (vertices.length >= 3) {
                                // K kenarı var (a-b)
                                const aIdx = vertices.length - 3;
                                const a = vertices[aIdx];

                                // K kenarının yönü (a'dan b'ye)
                                const kDirX = bOld.x - a.x;
                                const kDirY = bOld.y - a.y;

                                if (Math.abs(kDirX) > Math.abs(kDirY)) {
                                    // K yatay → L dikey olmalı → b'nin x'i c'nin x'ine eşit, y'si aynı
                                    newBx = cX;
                                    newBy = bOld.y;
                                } else {
                                    // K dikey → L yatay olmalı → b'nin y'si c'nin y'sine eşit, x'i aynı
                                    newBx = bOld.x;
                                    newBy = cY;
                                }
                            } else {
                                // Sadece 2 vertex var (b ve c), K kenarı yok
                                // b'den c'ye olan yöne göre karar ver
                                const dirX = cX - bOld.x;
                                const dirY = cY - bOld.y;

                                if (Math.abs(dirX) > Math.abs(dirY)) {
                                    // Yatay geliyor → dikey giriş
                                    newBx = cX;
                                    newBy = bOld.y;
                                } else {
                                    // Dikey geliyor → yatay giriş
                                    newBx = bOld.x;
                                    newBy = cY;
                                }
                            }

                            // b ve c vertex'lerini güncelle
                            vertices[bIdx] = new THREE.Vector3(newBx, newBy, 0);
                            vertices[cIdx] = new THREE.Vector3(cX, cY, 0);

                            // Arrow pozisyonunu da güncelle
                            arrowPosition.x = exitPointPosition.x;
                            arrowPosition.y = exitPointPosition.y;
                            permanentArrow.position.set(exitPointPosition.x, exitPointPosition.y, 0.2);
                        } else if (base.verticesRef.current.length === 1) {
                            // Sadece 1 vertex var, direkt güncelle
                            base.verticesRef.current[0] = new THREE.Vector3(exitPointPosition.x, exitPointPosition.y, 0);
                            arrowPosition.x = exitPointPosition.x;
                            arrowPosition.y = exitPointPosition.y;
                            permanentArrow.position.set(exitPointPosition.x, exitPointPosition.y, 0.2);
                        }
                    }

                    // ✅ cableShaftConnection oluştur (startShaft ve endShaft)
                    let cableShaftConnection = null;
                    if (shaftData) {
                        cableShaftConnection = {
                            endShaft: {
                                shaftId: shaftData.id,
                                shaftGroupId: shaftData.shaftGroupId,
                                floorPlanId: shaftData.floorPlanId,
                                floorPlanName: shaftData.floorPlanName,
                                arrowDirection: permanentArrow.userData.direction,
                                connectionPoint: exitPointPosition,
                                exitIndex: exitIndex
                            }
                        };

                        // ✅ ContinueFromShaft: Wire kablo bacasından başladıysa startShaft ekle
                        if (base.snapSourceRef.current?.type === 'cableShaftConnection' && base.snapSourceRef.current?.p) {
                            const startShaftInfo = base.snapSourceRef.current.p;
                            cableShaftConnection.startShaft = {
                                shaftId: startShaftInfo.shaftId,
                                shaftGroupId: startShaftInfo.shaftGroupId,
                                sourceWireId: startShaftInfo.sourceWireId,
                                connectionPoint: base.verticesRef.current[0] ? {
                                    x: base.verticesRef.current[0].x,
                                    y: base.verticesRef.current[0].y
                                } : null
                            };
                        }
                    } else if (base.snapSourceRef.current?.type === 'cableShaftConnection' && base.snapSourceRef.current?.p) {
                        // Wire sadece kablo bacasından başlıyor (arrow yok ama shaft'tan başladı)
                        const startShaftInfo = base.snapSourceRef.current.p;
                        cableShaftConnection = {
                            startShaft: {
                                shaftId: startShaftInfo.shaftId,
                                shaftGroupId: startShaftInfo.shaftGroupId,
                                sourceWireId: startShaftInfo.sourceWireId,
                                connectionPoint: base.verticesRef.current[0] ? {
                                    x: base.verticesRef.current[0].x,
                                    y: base.verticesRef.current[0].y
                                } : null
                            }
                        };
                    }

                    // Wire geometrisini oluştur
                    const wireGeometry = new THREE.BufferGeometry().setFromPoints(base.verticesRef.current);
                    const wireMaterial = new THREE.LineBasicMaterial({ color: base.wireColor || 0x00ff00 });
                    const permanentWire = new THREE.Line(wireGeometry, wireMaterial);

                    // ✅ Kaynak panel bilgilerini bul (CableShaftTypeMenu'de göstermek için)
                    const snapSource = base.snapSourceRef.current;
                    // Panel bağlantısından veya buat/switch userData'dan panelID al
                    const sourcePanelId = snapSource?.p?.panelID
                        || snapSource?.userData?.panelId || snapSource?.userData?.panelID
                        || snapSource?.parent?.userData?.panelID || null;
                    let sourcePanelName = null;
                    if (sourcePanelId) {
                        scene.traverse(obj => {
                            if (obj.userData?.id === sourcePanelId) {
                                sourcePanelName = obj.userData.name || 'Panel';
                            }
                        });
                    }
                    const lightingSubType = base.operationData?.initialItemType
                        || snapSource?.userData?.lightingSubType
                        || snapSource?.parent?.userData?.lightingSubType || null;
                    // ✅ Fixture hatları için: kaynak buat/switch'ten lightingLoadId al
                    const parentLightingLoadId = snapSource?.userData?.lightingLoadId
                        || snapSource?.parent?.userData?.lightingLoadId || null;

                    // Wire userData - kablo bacası bağlantı bilgisi dahil
                    permanentWire.userData = {
                        id: wireId,
                        type: 'floorTransitionWire',
                        isPartialWire: true,
                        arrowDirection: permanentArrow.userData.direction,
                        floorPlanId: shaftData?.floorPlanId,
                        floorPlanName: shaftData?.floorPlanName,
                        cableShaftConnection: cableShaftConnection,
                        wireName: base.operationData?.source?.name || base.operationData?.panelName || options.itemTypeName || 'Kablo',
                        wireColor: base.wireColor,
                        // ✅ Devam ettirme için command type bilgisi
                        sourceCommandType: options.commandType,
                        // ✅ Kaynak panel ve çıkış bilgisi
                        sourcePanelId: sourcePanelId,
                        sourcePanelName: sourcePanelName,
                        lightingSubType: lightingSubType,
                        // ✅ Fixture hatları için: kaynak load ID
                        parentLightingLoadId: parentLightingLoadId,
                        // ✅ Panel çizimi: kablo bacasından devam için panel bilgileri
                        panelName: base.operationData?.source?.name || base.operationData?.panelName || null,
                        purposeOfUse: base.operationData?.source?.purposeOfUse || base.operationData?.purposeOfUse || null,
                        aboneTuru: base.operationData?.source?.aboneTuru || base.operationData?.aboneTuru || null,
                    };
                    scene.add(permanentWire);

                    // ✅ Redux'a wire'ı kaydet (kablo bacası bağlantı bilgisi ile)
                    if (cableShaftConnection) {
                        const verticesForRedux = base.verticesRef.current.map(v => ({ x: v.x, y: v.y, z: v.z || 0 }));

                        // ✅ DEBUG: Log all shaft data before saving

                        base.dispatch(addWire({
                            id: wireId,
                            type: 'floorTransitionWire',
                            vertices: verticesForRedux,
                            layer: '0',
                            color: base.wireColor || 0x00ff00,
                            userData: {
                                isPartialWire: true,
                                arrowDirection: permanentArrow.userData.direction,
                                floorPlanId: shaftData?.floorPlanId,
                                floorPlanName: shaftData?.floorPlanName,
                                cableShaftConnection: cableShaftConnection,
                                wireName: base.operationData?.source?.name || base.operationData?.panelName || options.itemTypeName || 'Kablo',
                                wireColor: base.wireColor,
                                // ✅ Devam ettirme için command type bilgisi
                                sourceCommandType: options.commandType,
                                // ✅ Kaynak panel ve çıkış bilgisi
                                sourcePanelId: sourcePanelId,
                                sourcePanelName: sourcePanelName,
                                lightingSubType: lightingSubType,
                                // ✅ Fixture hatları için: kaynak load ID
                                parentLightingLoadId: parentLightingLoadId,
                                // ✅ Panel çizimi: kablo bacasından devam için panel bilgileri
                                panelName: base.operationData?.source?.name || base.operationData?.panelName || null,
                                purposeOfUse: base.operationData?.source?.purposeOfUse || base.operationData?.purposeOfUse || null,
                                aboneTuru: base.operationData?.source?.aboneTuru || base.operationData?.aboneTuru || null,
                            }
                        }));
                    } else if (!isInsideCableShaftRef.current) {
                        // ✅ Baca DIŞI: ok-hattını NORMAL wire olarak Redux'a kaydet
                        // (useWireRebuild ile geri yüklenir). Ucunda komponent yok, ok var.
                        // Ok'un kendisi Adım 4'te ayrıca persist edilecek; panel-exit
                        // entegrasyonu Adım 3d. isFloorTransition + arrowDirection işaretlenir.
                        const verticesForRedux = base.verticesRef.current.map(v => ({ x: v.x, y: v.y, z: v.z || 0 }));
                        permanentWire.userData = {
                            ...permanentWire.userData,
                            type: 'wire',
                            isFloorTransition: true,
                            arrowDirection: permanentArrow.userData.direction,
                            arrowRotation: permanentArrow.userData.rotation,
                        };
                        permanentArrow.userData.wireId = wireId;
                        base.dispatch(addWire({
                            id: wireId,
                            type: 'wire',
                            vertices: verticesForRedux,
                            layer: '0',
                            color: base.wireColor || 0x00ff00,
                            userData: {
                                isFloorTransition: true,
                                arrowDirection: permanentArrow.userData.direction,
                                arrowRotation: permanentArrow.userData.rotation,
                                wireColor: base.wireColor,
                                sourceCommandType: options.commandType,
                                sourcePanelId: sourcePanelId,
                                sourcePanelName: sourcePanelName,
                                lightingSubType: lightingSubType,
                                parentLightingLoadId: parentLightingLoadId,
                            },
                        }));

                        // ✅ Cross-floor işaretçi: yukarı/aşağı ok ise HEDEF katın izdüşüm
                        // noktasına "gelen ok" koy (kullanıcı oradan devam edebilecek — 3c).
                        try {
                            placeCrossFloorMarker({
                                scene,
                                sortedFloors,
                                sourceFloorPlanId: activeFloorPlanId,
                                direction: permanentArrow.userData.direction,
                                worldPos: arrowPosition,
                                wireColor: base.wireColor,
                                wireId,
                                sourceCommandType: options.commandType,
                            });
                        } catch (e) { /* cross-floor placement hatası — hattı bozma */ }

                        // ✅ Ok-hattındaki prizlere/anahtarlara buat ekle (per-type callback).
                        // Ok hat sonu olduğu için TÜM item'lar buat alır (normalde son hariç);
                        // useBuatTypeSync tipi (yuvarlak/kare) hat sayısına göre düzeltir.
                        try {
                            if (typeof options.onArrowFinalize === 'function') {
                                options.onArrowFinalize({
                                    items: base.itemsRef.current,
                                    wireId,
                                    panelID: sourcePanelId,
                                    color: base.wireColor,
                                });
                            }
                        } catch (e) { /* buat ekleme hatası — hattı bozma */ }
                    }

                    // Mevcut temp line'ı kaldır
                    if (base.tempLineRef.current) {
                        scene.remove(base.tempLineRef.current);
                        base.tempLineRef.current.geometry.dispose();
                        base.tempLineRef.current.material.dispose();
                        base.tempLineRef.current = null;
                    }

                    // ✅ Snap marker temizle
                    if (exitPointSnapMarkerRef.current) {
                        scene.remove(exitPointSnapMarkerRef.current);
                        exitPointSnapMarkerRef.current.geometry?.dispose();
                        exitPointSnapMarkerRef.current.material?.dispose();
                        exitPointSnapMarkerRef.current = null;
                    }

                    // Kablo bacası ref'lerini temizle
                    isInsideCableShaftRef.current = false;
                    currentCableShaftRef.current = null;
                    selectedExitPointRef.current = null;

                    // ✅ ContinueFromShaft modunu temizle
                    if (continueFromShaftModeRef.current) {
                        continueFromShaftModeRef.current = false;
                        continueFromShaftDataRef.current = null;
                    }
                }

                // Preview sembolünü temizle
                if (base.previewSymbolRef.current) {
                    { const _ps = base.previewSymbolRef.current; if (_ps.parent) _ps.parent.remove(_ps); else scene.remove(_ps); }
                    base.previewSymbolRef.current.traverse((child) => {
                        if (child.geometry) child.geometry.dispose();
                        if (child.material) child.material.dispose();
                    });
                    base.previewSymbolRef.current = null;
                }
                
                // Wire marker'ları temizle
                if (base.wireMarkersRef.current && base.wireMarkersRef.current.length > 0) {
                    base.wireMarkersRef.current.forEach(marker => {
                        if (marker) {
                            scene.remove(marker);
                            if (marker.geometry) marker.geometry.dispose();
                            if (marker.material) marker.material.dispose();
                        }
                    });
                    base.wireMarkersRef.current = [];
                }
                base.previewMarkersRef.current = [];
                
                // State'leri sıfırla (Redux işlemi YAPILMADAN)
                base.setStep(0);
                base.undoStackRef.current = [];
                base.redoStackRef.current = [];
                base.itemsRef.current = [];
                base.verticesRef.current = [];
                base.startRef.current = false;
                base.snapSourceRef.current = null;
                
                const directionText = permanentArrow.userData.direction === 'up' ? 'Yukarı kata' : 
                                     permanentArrow.userData.direction === 'down' ? 'Aşağı kata' : 
                                     permanentArrow.userData.direction === 'left' ? 'Sola' : 'Sağa';
                base.dispatch(setCommandMessage(`Hat ${directionText} geçiş oku ile sonlandırıldı. Devam katında hattı başlatın.`));
                base.dispatch(resetOperation());
            }
            return; // ✅ Ok sembolü varsa burada dur, normal akışa devam etme
        }
        
        // ✅ Normal akış: Ok ön izlemesini temizle (varsa ama aktif değilse)
        if (arrowPreviewRef.current) {
            scene.remove(arrowPreviewRef.current);
            arrowPreviewRef.current.traverse(child => {
                if (child.geometry) child.geometry.dispose();
                if (child.material) child.material.dispose();
            });
            arrowPreviewRef.current = null;
            arrowPreviewActiveRef.current = false;
        }

        base.setStep(0);

        // ✅ REDO modunda ve henüz tüm vertexler restore edilmediyse Enter'ı ENGELLE
        // Kullanıcı Ctrl+Y ile tüm vertexleri tamamlamalı
        if (base.operationData?.isRedo && base.redoStackRef.current.length > 0) {
            base.dispatch(setCommandMessage('REDO: Ctrl+Y ile tüm vertexleri ekleyin'));
            return;
        }

        // ✅ REDO: Machine pozisyon bilgileri varsa manuel olarak item ekle
        if (base.operationData?.isRedo && base.operationData?.machineData && !base.itemsRef.current.length) {
 
            const machineData = base.operationData.machineData;
            const machinePos = machineData.position;

            // Machine item'ı manuel olarak ekle (orijinal konum ve açı ile)
            base.itemsRef.current.push({
                position: new THREE.Vector3(machinePos.x, machinePos.y, machinePos.z || 0),
                type: machineData.machineType || 'Çamaşır Makinası',
                angle: machineData.angle,
                direction: machineData.direction,
                power: machineData.power
            });
            base.itemCountRef.current++;
        } else if (!base.itemsRef.current.length) {
             base.addItem(rotateOffsetRef.current);
        }

        let wireResult = null;
        if (base.previewEndPointRef.current && base.verticesRef.current.length > 0) {
            // Simplify vertices before finishing
            const simplifyVertices = (vertices, items) => {
                if (vertices.length < 3) return vertices;

                const newVertices = [vertices[0]];
                const tolerance = 0.01;
                const angleTolerance = 0.01;

                for (let i = 1; i < vertices.length - 1; i++) {
                    const vertex = vertices[i];

                    const hasItem = items.some(item => {
                        const itemPos = item.position;
                        return vertex.distanceTo(itemPos) < tolerance;
                    });

                    if (hasItem) {
                        newVertices.push(vertex);
                        continue;
                    }

                    const prev = newVertices[newVertices.length - 1];
                    const next = vertices[i + 1];

                    const v1 = new THREE.Vector3().subVectors(vertex, prev).normalize();
                    const v2 = new THREE.Vector3().subVectors(next, vertex).normalize();

                    const angle = v1.angleTo(v2);

                    if (angle > angleTolerance) {
                        newVertices.push(vertex);
                    }
                }

                newVertices.push(vertices[vertices.length - 1]);
                return newVertices;
            };

            // Restore/redo sırasında sadeleştirme devre dışı (orijinal vertexler korunur)
            const shouldSimplify = options.simplifyCollinearVertices !== false;
            // ✅ operationData üzerinden güncel isRedo değerini al (closure problemi çözümü)
            const isRestoringOrRedo = options.isRestoring || options.isRedo || base.operationData?.isRedo;
            const finalVertices = (shouldSimplify && !isRestoringOrRedo)
                ? simplifyVertices(base.verticesRef.current, base.itemsRef.current)
                : base.verticesRef.current;

 
            if (finalVertices.length > 1) {
                 if (typeof base.onWireEnd === 'function') {
                     wireResult = base.onWireEnd(
                        finalVertices,
                        base.itemsRef.current,
                        base.snapSourceRef.current,
                        base.itemsRef.current[0],
                        null,
                        base.wireMarkersRef.current,
                        base.verticesRef.current // ✅ YENİ: orijinal vertices (simplify edilmemiş)
                    );
                }

                if (base.tempLineRef.current) {
                    base.tempLineRef.current.userData = {
                        ...(base.tempLineRef.current.userData || {}),
                        type: 'wire'
                    };
                }

                if (base.snapSourceRef.current?.type === 'panelConnection' && wireResult?.wireId) {
                    const sourcePanel = base.snapSourceRef.current.userData?.sourceObject;
                    if (sourcePanel && sourcePanel.userData?.id) {
                        updatePanelInputWire(scene, sourcePanel);
                        updatePanelTitles(scene, sourcePanel.userData.id);
                    }
                }

                // ✅ Preview wireMarker'ları temizle (permanent olanlar onWireEnd'de oluşturuluyor)
                if (base.wireMarkersRef.current && base.wireMarkersRef.current.length > 0) {
                    base.wireMarkersRef.current.forEach(marker => {
                        if (marker) {
                            scene.remove(marker);
                            if (marker.geometry) marker.geometry.dispose();
                            if (marker.material) marker.material.dispose();
                        }
                    });
                    base.wireMarkersRef.current = [];
                }
                base.previewMarkersRef.current = [];
            }
        }

        if (base.previewSymbolRef.current) {
            { const _ps = base.previewSymbolRef.current; if (_ps.parent) _ps.parent.remove(_ps); else scene.remove(_ps); }
            base.previewSymbolRef.current.traverse((child) => {
                if (child.geometry) child.geometry.dispose();
                if (child.material) child.material.dispose();
            });
            base.previewSymbolRef.current = null;
        }

        if (base.tempLineRef.current) {
            scene.remove(base.tempLineRef.current);
            base.tempLineRef.current.geometry.dispose();
            base.tempLineRef.current.material.dispose();
            base.tempLineRef.current = null;
        }

        const socketActions = base.undoStackRef.current.filter(action => action.type === 'socket');
        if (socketActions.length > 0) {
            const allComponents = [];
            const allFeeders = [];
            socketActions.forEach(action => {
                const { component, feeder } = action.command.data.after || action.command.data.before;
                allComponents.push(component);
                allFeeders.push(feeder);
            });

            base.dispatch(addBulk({
                components: allComponents,
                feeders: allFeeders
            }));
        }

        if (socketActions.length > 0) {
            const allActions = socketActions.map(action => action.command.toJSON());
            base.dispatch(addActions(allActions));
        }

        // ✅ Local undo/redo stack'lerini temizle
        base.undoStackRef.current = [];
        base.redoStackRef.current = [];
        base.itemsRef.current = [];
        base.verticesRef.current = [];

        // ✅ Wiring refs'leri temizle
        base.startRef.current = false;
        base.snapSourceRef.current = null;

        // ✅ Offset preview rehberleri (magenta/cyan dashed) ve snap marker'ları
        // sahneden temizle. Hat finalize olduktan sonra geçici görseller kalmamalı.
        // Ref'leri de null'la — sonraki çağrılarda stale referans kullanılmasın.
        try {
            SceneCleanupManager.cleanupOffsetLines(base.offsetLineRef, scene);
            SceneCleanupManager.cleanupRef(base.snapMarkerRef, scene);
            SceneCleanupManager.removeOrphanGuides(scene);
        } catch (e) {
            // cleanup hatası — devam et
        }

        // ✅ Defensive: tüm wire'larda ardışık duplicate vertex'leri temizle.
        // Trace follow, snap çakışması, gizmo drag artıkları zamanla sıfır-uzunluklu
        // segment bırakabiliyor → gizmo'ların üst üste binmesi ve panel exit
        // üzerinde stack'lenmiş vertex görünümü oluşuyordu.
        try { dedupAllWiresInScene(scene); } catch (e) { /* ignore */ }

        // ✅ NOT: resetOperation() handleWireEnd içinde çağrılıyor
        // (MainPanel ve Machine'de handleWireEnd = confirmPlacement yapısı var)

        console.groupEnd();
    };

    // H5-3: LOCAL Wire Vertex Undo-Redo (Ctrl+Z / Ctrl+Y çizim sırasında)
    // ayrı hook'a çıkarıldı — confirmPlacement closure üzerinden geçirilir
    useWireLocalUndoRedo({ scene, base, options, confirmPlacement });

    // H5-4 (Phase 2): Auto-select buat — autoSelectBuat varsa çizimi otomatik başlat
    useWireAutoSelectBuat({ scene, base, options });

    /**
     * ✅ CONTINUE FROM SHAFT LOGIC
     * Kablo bacasından devam etme modunu aktif et
     * ✅ SADECE bu hook'un commandType'ı ile eşleşiyorsa aktif et
     */
    useEffect(() => {
        // ✅ FIX: Sadece bu hook'un commandType'ı için continueFromShaft modunu aktif et
        const sourceCommandType = base.operationData?.sourceCommandType;
        const isForThisHook = sourceCommandType === options.commandType;

        if (!scene || !base.operationData?.continueFromShaft || !isForThisHook) {
            // Mod kapatıldıysa veya bu hook için değilse temizle
            if (continueFromShaftModeRef.current && (!base.operationData?.continueFromShaft || !isForThisHook)) {
                continueFromShaftModeRef.current = false;
                continueFromShaftDataRef.current = null;
            }
            return;
        }

        const { shaftId, shaftGroupId, sourceCable, sourceWireId, startPoint } = base.operationData;

  
        // Kablo bacasını bul
        let shaftGroup = null;
        scene.traverse((obj) => {
            if (obj.userData?.type === 'cableShaft' && obj.userData?.id === shaftId) {
                shaftGroup = obj;
            }
        });

        if (!shaftGroup) {
            console.warn('[ComponentWiring] Shaft not found:', shaftId);
            return;
        }

        const shaftData = shaftGroup.userData;
        if (!shaftData.diagonalStart || !shaftData.diagonalEnd) {
            console.warn('[ComponentWiring] Shaft has no diagonal:', shaftId);
            return;
        }

        // Modu aktif et
        continueFromShaftModeRef.current = true;
        continueFromShaftDataRef.current = {
            shaftId,
            shaftGroupId,
            sourceCable,
            sourceWireId,
            shaftGroup,
            shaftData
        };

        // Kablo bacasını aktif olarak ayarla (çıkış noktalarını göstermek için)
        currentCableShaftRef.current = shaftGroup;
        isInsideCableShaftRef.current = true;

        // Mevcut çıkış noktalarını topla
        const occupiedExitInfo = [];
        shaftGroup.traverse((child) => {
            if (child.userData?.type === 'shaftExitPoint' && child.userData?.isOccupied) {
                occupiedExitInfo.push({
                    wireId: child.userData.wireId,
                    direction: child.userData.direction,
                    position: child.position.clone()
                });
            }
        });

        // Mevcut çıkış noktalarını temizle
        const toRemove = [];
        shaftGroup.traverse((child) => {
            if (child.userData?.type === 'shaftExitPoint') {
                toRemove.push(child);
            }
        });
        toRemove.forEach(child => {
            shaftGroup.remove(child);
            if (child.geometry) child.geometry.dispose();
            if (child.material) child.material.dispose();
        });

        // Yeni çıkış sayısı (mevcut + 1)
        const occupiedCount = occupiedExitInfo.length;
        const newExitCount = occupiedCount + 1;
        const exitIndex = occupiedCount; // Yeni çıkış en sonda

        // Çıkış noktalarını yeniden oluştur
        for (let i = 0; i < newExitCount; i++) {
            const t = (i + 1) / (newExitCount + 1);
            const position = getPointOnDiagonal(shaftData.diagonalStart, shaftData.diagonalEnd, t);

            const isOccupied = i < occupiedCount;
            const wireInfo = occupiedExitInfo[i] || null;
            const isNewExitPoint = i === exitIndex;

            const circleGeometry = new THREE.CircleGeometry(2, 16);
            const circleMaterial = new THREE.MeshBasicMaterial({
                color: isNewExitPoint ? 0x00ff00 : (isOccupied ? 0xff6600 : 0x00ff00),
                side: THREE.DoubleSide,
                transparent: true,
                opacity: 0.8
            });

            const circle = new THREE.Mesh(circleGeometry, circleMaterial);
            circle.position.copy(position);
            circle.name = `shaft-exit-point-${i}`;
            circle.userData = {
                type: 'shaftExitPoint',
                shaftId: shaftData.id,
                exitIndex: i,
                isExitPoint: true,
                isOccupied: isOccupied,
                isNewExitPoint: isNewExitPoint,
                wireId: wireInfo?.wireId || null,
                direction: wireInfo?.direction || null,
                isShaftPart: true
            };

            shaftGroup.add(circle);

            // Yeni çıkış noktasını seçili olarak ayarla
            if (isNewExitPoint) {
                selectedExitPointRef.current = circle;
            }

            // Mevcut wire'ların vertex'lerini ve arrow'larını güncelle
            if (isOccupied && wireInfo?.wireId) {
                let oldLastX = null, oldLastY = null;
                scene.traverse((obj) => {
                    if (obj.userData?.id === wireInfo.wireId && obj.geometry?.attributes?.position) {
                        const posAttr = obj.geometry.attributes.position;
                        const numVerts = posAttr.count;
                        if (numVerts > 0) {
                            oldLastX = posAttr.getX(numVerts - 1);
                            oldLastY = posAttr.getY(numVerts - 1);
                        }
                    }
                });

                scene.traverse((obj) => {
                    if (obj.userData?.id === wireInfo.wireId && obj.geometry?.attributes?.position) {
                        const posAttr = obj.geometry.attributes.position;
                        const numVerts = posAttr.count;

                        if (numVerts >= 2) {
                            const cIdx = numVerts - 1;
                            const bIdx = numVerts - 2;

                            const cX = position.x;
                            const cY = position.y;
                            const bOldX = posAttr.getX(bIdx);
                            const bOldY = posAttr.getY(bIdx);

                            let newBx, newBy;

                            if (numVerts >= 3) {
                                const aIdx = numVerts - 3;
                                const aX = posAttr.getX(aIdx);
                                const aY = posAttr.getY(aIdx);

                                const kDirX = bOldX - aX;
                                const kDirY = bOldY - aY;

                                if (Math.abs(kDirX) > Math.abs(kDirY)) {
                                    newBx = cX;
                                    newBy = bOldY;
                                } else {
                                    newBx = bOldX;
                                    newBy = cY;
                                }
                            } else {
                                const dirX = cX - bOldX;
                                const dirY = cY - bOldY;

                                if (Math.abs(dirX) > Math.abs(dirY)) {
                                    newBx = cX;
                                    newBy = bOldY;
                                } else {
                                    newBx = bOldX;
                                    newBy = cY;
                                }
                            }

                            posAttr.setXYZ(bIdx, newBx, newBy, 0);
                            posAttr.setXYZ(cIdx, cX, cY, 0);
                            posAttr.needsUpdate = true;
                            obj.geometry.computeBoundingSphere();
                        } else if (numVerts === 1) {
                            posAttr.setXYZ(0, position.x, position.y, 0);
                            posAttr.needsUpdate = true;
                            obj.geometry.computeBoundingSphere();
                        }
                    }
                });

                // Arrow'ları güncelle
                if (oldLastX !== null && oldLastY !== null) {
                    scene.traverse((obj) => {
                        if (obj.userData?.type === 'floorTransitionArrow') {
                            const arrowPos = obj.position;
                            const dx = arrowPos.x - oldLastX;
                            const dy = arrowPos.y - oldLastY;
                            if (Math.sqrt(dx*dx + dy*dy) < 5) {
                                obj.position.set(position.x, position.y, obj.position.z);
                            }
                        }
                    });
                }
            }
        }

        // exitCount'u güncelle
        shaftData.exitCount = newExitCount;
        shaftData.occupiedExitCount = occupiedCount;

        // Snap marker oluştur
        if (!exitPointSnapMarkerRef.current) {
            const markerGeometry = new THREE.RingGeometry(4, 6, 24);
            const markerMaterial = new THREE.MeshBasicMaterial({
                color: 0xffff00,
                side: THREE.DoubleSide,
                transparent: true,
                opacity: 0.9
            });
            exitPointSnapMarkerRef.current = new THREE.Mesh(markerGeometry, markerMaterial);
            exitPointSnapMarkerRef.current.userData = { type: 'exitPointSnapMarker' };
            scene.add(exitPointSnapMarkerRef.current);
        }

        // Snap marker'ı yeni çıkış noktasına konumlandır
        if (selectedExitPointRef.current) {
            exitPointSnapMarkerRef.current.position.set(
                selectedExitPointRef.current.position.x,
                selectedExitPointRef.current.position.y,
                0.25
            );
            exitPointSnapMarkerRef.current.visible = true;
        }


    }, [scene, base.operationData?.continueFromShaft, base.operationData?.shaftId, base.operationData?.sourceCommandType, options.commandType]);

    /**
     * ✅ RESTORE VERTICES LOGIC
     * restoreVertices varsa vertices'leri geri yükle (undo sonrası)
     */
    useEffect(() => {
        // ✅ Infinite loop prevention: Skip if already processed this restore ID
        const currentRestoreId = base.operationData?._restoreId;
        if (currentRestoreId && lastRestoredIdRef.current === currentRestoreId) {
             return;
        }

        const hasRestoreVertices = base.operationData && Array.isArray(base.operationData.restoreVertices) && base.operationData.restoreVertices.length > 0;
        const canRestoreIfStarted = base.startRef.current && base.verticesRef.current && base.verticesRef.current.length <= 1;

        if (
            base.commandType === options.commandType &&
            base.operationData?.initialPoint &&
            base.operationData?.source &&
            base.enabled() &&
            hasRestoreVertices &&
            (
                !base.startRef.current || // henüz başlamamış
                canRestoreIfStarted // başlamış ama sadece initial point var
            )
        ) {
            // ✅ Mark this restore as processed
            lastRestoredIdRef.current = currentRestoreId;

            // ✅ UNDO modunda (isRedo === false) ilk vertex'i MEVCUT yeşil noktadan al
            // Çünkü updatePanelExitPoints wire'ları öteledikten sonra eski restoreVertices[0] geçersiz
            const isUndoMode = base.operationData.isRedo === false;

            if (isUndoMode) {
                // UNDO modu: İlk vertex'i mevcut exit point'ten hesapla
                let correctFirstVertex = null;

                // useBaseWiring zaten çalıştıysa (verticesRef.length === 1), onu kullan
                if (base.verticesRef.current.length === 1) {
                    correctFirstVertex = base.verticesRef.current[0].clone();
                } else {
                    // useBaseWiring henüz çalışmadıysa, exit point'i kendimiz hesapla
                    const panelID = base.operationData.source?.panelID;
                    if (panelID && scene) {
                        let panel = null;
                        scene.traverse(obj => {
                            if (obj.userData?.id === panelID) panel = obj;
                        });

                        if (panel) {
                            const exitPoints = findPanelExitPoints(panel);
                            if (exitPoints.length > 0) {
                                // addPosition'a göre exit point seç
                                const addPos = options.addPosition || 'end';
                                const startExitPoint = addPos === 'end'
                                    ? exitPoints[0]
                                    : exitPoints[exitPoints.length - 1];

                                correctFirstVertex = new THREE.Vector3();
                                startExitPoint.getWorldPosition(correctFirstVertex);
                            }
                        }
                    }
                }

                // Eğer doğru exit point bulunduysa, ilk ve ikinci vertex'i güncelle
                if (correctFirstVertex) {
                    // Offset hesapla: yeni ilk vertex - eski ilk vertex
                    const oldFirstVertex = new THREE.Vector3(
                        base.operationData.restoreVertices[0].x,
                        base.operationData.restoreVertices[0].y,
                        base.operationData.restoreVertices[0].z
                    );
                    const offset = correctFirstVertex.clone().sub(oldFirstVertex);

                    // İkinci vertex'i de aynı offset ile ötele
                    const remainingVertices = base.operationData.restoreVertices.slice(1).map((v, index) => {
                        const vec = new THREE.Vector3(v.x, v.y, v.z);
                        if (index === 0) { // İkinci vertex (slice sonrası index 0)
                            vec.add(offset);
                        }
                        return vec;
                    });
                    base.verticesRef.current = [correctFirstVertex, ...remainingVertices];
                } else {
                    // Fallback: tüm restoreVertices'i kullan
                    base.verticesRef.current = base.operationData.restoreVertices.map(v => new THREE.Vector3(v.x, v.y, v.z));
                }
            } else {
                // Normal akış veya REDO: tüm vertices'leri restoreVertices'ten al
                base.verticesRef.current = base.operationData.restoreVertices.map(v => new THREE.Vector3(v.x, v.y, v.z));
            }

            // Preview ve final point'leri güncelle
            if (base.verticesRef.current.length > 0) {
                base.previewEndPointRef.current = base.verticesRef.current[base.verticesRef.current.length - 1].clone();
                base.finalPointRef.current = base.previewEndPointRef.current.clone();
            }

            // Temp preview line oluştur
            if (base.verticesRef.current.length > 1) {
                // Önce eski temp line'ı temizle
                if (base.tempLineRef.current) {
                    scene.remove(base.tempLineRef.current);
                    base.tempLineRef.current.geometry.dispose();
                    base.tempLineRef.current.material.dispose();
                }

                const geometry = new THREE.BufferGeometry().setFromPoints(base.verticesRef.current);
                const previewColorSetting = settings?.socket?.wirePreviewColor ?? base.wireColor;
                const parsedPreview = parseColor(previewColorSetting);
                const material = new THREE.LineBasicMaterial({ color: parsedPreview.colorNumber, linewidth: 4, transparent: parsedPreview.alpha < 1, opacity: parsedPreview.alpha, depthTest: false });
                base.tempLineRef.current = new THREE.Line(geometry, material);
                base.tempLineRef.current.name = 'tempSocketWirePreview';
                scene.add(base.tempLineRef.current);
            }

            // ✅ Local undo stack'i restore edilen vertex'lerle doldur (ÖNCE)
            // Use proximity matching because socket scene objects may have an internal
            // offset relative to the wire vertex. Matching by exact coordinates caused
            // an off-by-one appearance where sockets appeared one vertex late.
            base.undoStackRef.current = [];
            const socketInfos = base.operationData.socketInfos || [];
            const lightSwitchInfos = base.operationData.lightSwitchInfos || [];
            const buatInfos = base.operationData.buatInfos || [];

            const TOLERANCE = 1.0; // world units; adjust if needed
            const processedBuatIds = new Set();
            const processedSocketIds = new Set();
            const processedLightSwitchIds = new Set();

            // Iterate vertices (skip the very first initial point)
            for (let vi = 1; vi < base.verticesRef.current.length; vi++) {
                const v = base.verticesRef.current[vi];

                // Find sockets whose stored position is within tolerance to this vertex
                const socketsHere = socketInfos.filter(si => {
                    if (!si || !si.position) return false;
                    if (processedSocketIds.has(si.id)) return false;
                    const dx = (si.position.x || 0) - (v.x || 0);
                    const dy = (si.position.y || 0) - (v.y || 0);
                    const dz = (si.position.z || 0) - (v.z || 0);
                    return Math.sqrt(dx * dx + dy * dy + dz * dz) <= TOLERANCE;
                });

                // Find lightSwitches whose stored position is within tolerance to this vertex
                const lightSwitchesHere = lightSwitchInfos.filter(lsi => {
                    if (!lsi || !lsi.position) return false;
                    if (processedLightSwitchIds.has(lsi.id)) return false;
                    const dx = (lsi.position.x || 0) - (v.x || 0);
                    const dy = (lsi.position.y || 0) - (v.y || 0);
                    const dz = (lsi.position.z || 0) - (v.z || 0);
                    return Math.sqrt(dx * dx + dy * dy + dz * dz) <= TOLERANCE;
                });

                if (socketsHere.length === 0 && lightSwitchesHere.length === 0) {
                    // No socket/lightSwitch at this vertex -> push a simple vertex action
                    base.undoStackRef.current.push({ type: 'vertex', vertex: v.clone(), vertexCountBefore: vi });
                } else {
                    // There are sockets at this vertex -> create socket commands and
                    // push socket actions only (each socket action will remove its
                    // associated vertex when undone). This makes a single local undo
                    // remove both the socket and its vertex, avoiding the "every two
                    // undos" behaviour observed previously.
                    for (let sidx = 0; sidx < socketsHere.length; sidx++) {
                        const socketInfo = socketsHere[sidx];
                        // mark processed so we don't recreate duplicates
                        processedSocketIds.add(socketInfo.id);
                        try {
                            // Align recreated socket exactly to the restored vertex to avoid offsets
                            const p2 = v.clone();
                            const p1 = base.verticesRef.current[vi - 1] || p2;
                            const direction = new THREE.Vector3().subVectors(p2, p1);
                            const angle = Math.atan2(direction.y, direction.x);
                            const itemPower = socketInfo.power || (base.itemTypeRef.current && base.itemTypeRef.current.power) || 300;
                            const componentId = socketInfo.id || generateUniqueId('socket');
                            const sourcePanelId = base.snapSourceRef.current?.p?.panelID;

                            const newComponent = ComponentFactory.createSocket(itemPower, {
                                id: componentId,
                                name: socketInfo.type,
                                position: { x: p2.x, y: p2.y, z: 0 },
                                rotation: { x: 0, y: 0, z: angle },
                                electrical: { installedPower: itemPower, phaseCount: 1 }
                            });

                            const itemVertices = [p1, p2].map(vv => ({ x: vv.x, y: vv.y, z: vv.z }));
                            const cableLength = p1.distanceTo ? Math.round(p1.distanceTo(p2)) : 0;
                            const newFeeder = FeederFactory.createFeederForLoad('socket', itemPower, sourcePanelId, componentId, {
                                cable: { length: Math.round(cableLength / 10), route: itemVertices },
                                electrical: { phaseCount: 1, voltage: 220 },
                                vertices: itemVertices
                            });

                            const itemCommand = base.createCommand('ADD', newComponent, newFeeder, {
                                socketPosition: [p2.x, p2.y, 0],
                                rotation: angle,
                                dir: base.flipDirectionRef.current,
                                color: base.wireColor,
                                socketType: socketInfo.type,
                                rotateValue: socketInfo.rotateValue || 0, // ✅ R tuşu rotasyonu
                                wireVertices: itemVertices
                            });

                            itemCommand.execute(scene, base.dispatch, true);

                            // ✅ Önce buat'ı oluştur (undoStack'e eklemek için)
                            let buatMesh = null;
                            const matchingBuat = buatInfos.find(bi => {
                                if (!bi || !bi.position || processedBuatIds.has(bi.id)) return false;
                                if (bi.socketLoadId === componentId || bi.socketLoadId === socketInfo.id) return true;
                                const dx = (bi.position.x || 0) - (p2.x || 0);
                                const dy = (bi.position.y || 0) - (p2.y || 0);
                                return Math.sqrt(dx * dx + dy * dy) <= TOLERANCE;
                            });

                            if (matchingBuat) {
                                processedBuatIds.add(matchingBuat.id);
                                try {
                                    const buatPosition = { x: matchingBuat.position.x, y: matchingBuat.position.y };
                                    // Orijinal rengi kullan (operationData'dan veya function'a göre)
                                    const buatColor = base.operationData?.wireColor ||
                                        (matchingBuat.function === 'simpleSocket' ? 0xff0000 : 0x0045ff);

                                    if (matchingBuat.buatType === 'square') {
                                        buatMesh = singleSquareBuat(scene, buatPosition, buatColor, matchingBuat.id);
                                    } else {
                                        buatMesh = singleCircleBuat(buatPosition, matchingBuat.panelID, matchingBuat.id, buatColor);
                                    }

                                    if (buatMesh) {
                                        buatMesh.userData = {
                                            ...buatMesh.userData,
                                            id: matchingBuat.id,
                                            type: matchingBuat.type || 'buat',
                                            buatType: matchingBuat.buatType || 'circle',
                                            socketLoadId: componentId,
                                            wireID: matchingBuat.wireID,
                                            panelID: matchingBuat.panelID,
                                            function: matchingBuat.function
                                        };
                                        if (matchingBuat.buatType !== 'square') {
                                            scene.add(buatMesh);
                                        }
                                    }
                                } catch (buatErr) {
                                    // Buat creation failed
                                }
                            }

                            // ✅ Socket action'ını undoStack'e ekle (buatMesh referansıyla birlikte)
                            base.undoStackRef.current.push({
                                type: 'socket',
                                command: itemCommand,
                                vertex: p2.clone(),
                                verticesAdded: 1,
                                vertexCountBefore: vi, // ✅ Snapshot: undo truncate hedefi
                                itemData: {
                                    position: p2.clone(),
                                    type: socketInfo.type,
                                    direction: direction,
                                    power: itemPower,
                                    uuid: componentId
                                },
                                buatMesh: buatMesh,
                                buatInfo: matchingBuat
                            });

                            base.itemsRef.current.push({ position: p2.clone(), type: socketInfo.type, rotateValue: socketInfo.rotateValue || 0, id: componentId });
                            base.itemCountRef.current++;
                        } catch (err) {
                            // Socket restore per-vertex failed
                        }
                    }

                    // LightSwitch restore logic
                    for (let lsidx = 0; lsidx < lightSwitchesHere.length; lsidx++) {
                        const lightSwitchInfo = lightSwitchesHere[lsidx];
                        processedLightSwitchIds.add(lightSwitchInfo.id);
                        try {
                            const p2 = v.clone();
                            const p1 = base.verticesRef.current[vi - 1] || p2;
                            const direction = new THREE.Vector3().subVectors(p2, p1);
                            const angle = Math.atan2(direction.y, direction.x);
                            const itemPower = lightSwitchInfo.power || (base.itemTypeRef.current && base.itemTypeRef.current.power) || 300;
                            const componentId = lightSwitchInfo.id || generateUniqueId('lightSwitch');
                            const sourcePanelId = base.snapSourceRef.current?.p?.panelID;

                            const newComponent = ComponentFactory.createLightSwitch(itemPower, {
                                id: componentId,
                                name: lightSwitchInfo.type,
                                position: { x: p2.x, y: p2.y, z: 0 },
                                rotation: { x: 0, y: 0, z: angle },
                                electrical: { installedPower: itemPower, phaseCount: 1 }
                            });

                            const itemVertices = [p1, p2].map(vv => ({ x: vv.x, y: vv.y, z: vv.z }));
                            const cableLength = p1.distanceTo ? Math.round(p1.distanceTo(p2)) : 0;
                            const newFeeder = FeederFactory.createFeederForLoad('lighting', itemPower, sourcePanelId, componentId, {
                                cable: { length: Math.round(cableLength / 10), route: itemVertices },
                                electrical: { phaseCount: 1, voltage: 220 },
                                vertices: itemVertices
                            });

                            const itemCommand = base.createCommand('ADD', newComponent, newFeeder, {
                                lightSwitchPosition: [p2.x, p2.y, 0],
                                rotation: angle,
                                dir: base.flipDirectionRef.current,
                                color: base.wireColor,
                                lightSwitchType: lightSwitchInfo.type,
                                rotateValue: lightSwitchInfo.rotateValue || 0, // ✅ R tuşu rotasyonu
                                wireVertices: itemVertices
                            });

                            itemCommand.execute(scene, base.dispatch, true);

                            // ✅ Önce buat'ı oluştur (undoStack'e eklemek için)
                            let buatMeshLS = null;
                            const matchingBuatLS = buatInfos.find(bi => {
                                if (!bi || !bi.position || processedBuatIds.has(bi.id)) return false;
                                if (bi.socketLoadId === componentId || bi.socketLoadId === lightSwitchInfo.id) return true;
                                const dx = (bi.position.x || 0) - (p2.x || 0);
                                const dy = (bi.position.y || 0) - (p2.y || 0);
                                return Math.sqrt(dx * dx + dy * dy) <= TOLERANCE;
                            });

                            if (matchingBuatLS) {
                                processedBuatIds.add(matchingBuatLS.id);
                                try {
                                    const buatPosition = { x: matchingBuatLS.position.x, y: matchingBuatLS.position.y };
                                    // Orijinal rengi kullan (operationData'dan veya function'a göre)
                                    const buatColor = base.operationData?.wireColor ||
                                        (matchingBuatLS.function === 'simpleSocket' ? 0xff0000 : 0x0045ff);

                                    if (matchingBuatLS.buatType === 'square') {
                                        buatMeshLS = singleSquareBuat(scene, buatPosition, buatColor, matchingBuatLS.id);
                                    } else {
                                        buatMeshLS = singleCircleBuat(buatPosition, matchingBuatLS.panelID, matchingBuatLS.id, buatColor);
                                    }
                                    if (buatMeshLS) {
                                        buatMeshLS.userData = {
                                            ...buatMeshLS.userData,
                                            id: matchingBuatLS.id,
                                            type: matchingBuatLS.type || 'buat',
                                            buatType: matchingBuatLS.buatType || 'circle',
                                            socketLoadId: componentId,
                                            wireID: matchingBuatLS.wireID,
                                            panelID: matchingBuatLS.panelID,
                                            function: matchingBuatLS.function
                                        };
                                        if (matchingBuatLS.buatType !== 'square') {
                                            scene.add(buatMeshLS);
                                        }
                                    }
                                } catch (buatErr) {
                                    // LightSwitch Buat restore failed
                                }
                            }

                            // ✅ LightSwitch action'ını undoStack'e ekle (buatMesh referansıyla birlikte)
                            base.undoStackRef.current.push({
                                type: 'lightSwitch',
                                command: itemCommand,
                                vertex: p2.clone(),
                                verticesAdded: 1,
                                vertexCountBefore: vi, // ✅ Snapshot: undo truncate hedefi
                                itemData: {
                                    position: p2.clone(),
                                    type: lightSwitchInfo.type,
                                    direction: direction,
                                    power: itemPower,
                                    uuid: componentId
                                },
                                buatMesh: buatMeshLS,
                                buatInfo: matchingBuatLS
                            });

                            base.itemsRef.current.push({ position: p2.clone(), type: lightSwitchInfo.type, rotateValue: lightSwitchInfo.rotateValue || 0, id: componentId });
                            base.itemCountRef.current++;
                        } catch (err) {
                            // LightSwitch restore per-vertex failed
                        }
                    }
                }
            }

            // ✅ SOCKET/MACHINE REDO: vertexHistory'yi redoStack'e yükle
            if (base.operationData.isRedo && base.operationData.vertexHistory && base.operationData.vertexHistory.length > 0) {
                 const TOLERANCE = 1.0;
                const processedSocketIdsRedo = new Set();
                const processedLightSwitchIdsRedo = new Set();
                const processedBuatIdsRedo = new Set();

                // ✅ CRITICAL: redoStack LIFO (pop from end), so REVERSE vertexHistory!
                // vertexHistory: [v1, v2, v3, v4, v5, v6]
                // redoStack: [v6, v5, v4, v3, v2, v1] ← REVERSED
                // pop() → v1, pop() → v2, ... ✅ CORRECT ORDER
                const redoActions = [];

                base.operationData.vertexHistory.forEach((v, index) => {
                    const vertex = new THREE.Vector3(v.x, v.y, v.z);

                    // ✅ Bu vertex'te socket var mı kontrol et
                    const socketsHere = socketInfos.filter(si => {
                        if (!si || !si.position) return false;
                        if (processedSocketIdsRedo.has(si.id)) return false;
                        const dx = (si.position.x || 0) - (v.x || 0);
                        const dy = (si.position.y || 0) - (v.y || 0);
                        const dz = (si.position.z || 0) - (v.z || 0);
                        return Math.sqrt(dx * dx + dy * dy + dz * dz) <= TOLERANCE;
                    });

                    // ✅ Bu vertex'te lightSwitch var mı kontrol et
                    const lightSwitchesHere = lightSwitchInfos.filter(lsi => {
                        if (!lsi || !lsi.position) return false;
                        if (processedLightSwitchIdsRedo.has(lsi.id)) return false;
                        const dx = (lsi.position.x || 0) - (v.x || 0);
                        const dy = (lsi.position.y || 0) - (v.y || 0);
                        const dz = (lsi.position.z || 0) - (v.z || 0);
                        return Math.sqrt(dx * dx + dy * dy + dz * dz) <= TOLERANCE;
                    });

                    if (socketsHere.length === 0 && lightSwitchesHere.length === 0) {
                        // Socket/lightSwitch yok, sadece vertex
                        redoActions.push({ type: 'vertex', vertex: vertex });
                    } else {
                        // Socket var, socket action'lar ekle
                        socketsHere.forEach(socketInfo => {
                            processedSocketIdsRedo.add(socketInfo.id);

                            // ✅ Bu socket için buat var mı kontrol et
                            const matchingBuatRedo = buatInfos.find(bi => {
                                if (!bi || !bi.position || processedBuatIdsRedo.has(bi.id)) return false;
                                if (bi.socketLoadId === socketInfo.id) return true;
                                const dx = (bi.position.x || 0) - (v.x || 0);
                                const dy = (bi.position.y || 0) - (v.y || 0);
                                return Math.sqrt(dx * dx + dy * dy) <= TOLERANCE;
                            });

                            if (matchingBuatRedo) {
                                processedBuatIdsRedo.add(matchingBuatRedo.id);
                            }

                            redoActions.push({
                                type: 'socket',
                                vertex: vertex,
                                socketInfo: socketInfo,
                                buatInfo: matchingBuatRedo || null
                            });
                        });

                        // LightSwitch var, lightSwitch action'lar ekle
                        lightSwitchesHere.forEach(lightSwitchInfo => {
                            processedLightSwitchIdsRedo.add(lightSwitchInfo.id);

                            // ✅ Bu lightSwitch için buat var mı kontrol et
                            const matchingBuatRedoLS = buatInfos.find(bi => {
                                if (!bi || !bi.position || processedBuatIdsRedo.has(bi.id)) return false;
                                if (bi.socketLoadId === lightSwitchInfo.id) return true;
                                const dx = (bi.position.x || 0) - (v.x || 0);
                                const dy = (bi.position.y || 0) - (v.y || 0);
                                return Math.sqrt(dx * dx + dy * dy) <= TOLERANCE;
                            });

                            if (matchingBuatRedoLS) {
                                processedBuatIdsRedo.add(matchingBuatRedoLS.id);
                            }

                            redoActions.push({
                                type: 'lightSwitch',
                                vertex: vertex,
                                lightSwitchInfo: lightSwitchInfo,
                                buatInfo: matchingBuatRedoLS || null
                            });
                        });
                    }
                });

                // ✅ REVERSE for LIFO
                base.redoStackRef.current = redoActions.reverse();

           
            } else {
                base.redoStackRef.current = [];
            }



            // Source'u set et
            const snapSource = {
                type: base.operationData.source.type,
                p: base.operationData.source.p,
                no: base.operationData.source.no,
                panelID: base.operationData.source.p?.panelID || base.operationData.source.panelID,
                // ✅ Dallanma (vertex/lightingBuat) kaynak alanlarını koru — yoksa branch hatta
                // re-draw + (partial) Enter'da confirmPlacement source.parent'ı bulamayıp
                // parentId null → erken return → hat finalize olmuyor, undo resetleniyordu.
                lightingLoadId: base.operationData.source.lightingLoadId,
                socketLoadId: base.operationData.source.socketLoadId,
                wireID: base.operationData.source.wireID,
                parent: base.operationData.source.parent || null,
                userData: {
                    type: 'panelConnection',
                    sourceObject: getObjectById(scene, base.operationData.source.p?.panelID),
                },
            };

            base.snapSourceRef.current = snapSource;
            base.startRef.current = true;
            base.setStep(1);

            base.dispatch(setCommandMessage("Socket hattı geri yüklendi. Devam etmek için Enter'a basın, Ctrl+Z ile vertex silebilirsiniz."));
        }
    }, [base.operationData, base.operationData && base.operationData._restoreId, base.enabled, scene, base.commandType]);

    useEffect(() => {
        // ✅ enabled() kontrolünü kaldır - timing issue'ya sebep oluyor
        // ✅ continueFromShaftMode kontrolü - SADECE bu hook'un commandType'ı ile eşleşiyorsa aktif et
        const isContinueFromShaft = base.operationData?.continueFromShaft === true;
        const sourceCommandType = base.operationData?.sourceCommandType;

        // ✅ FIX: continueFromShaft modunda sadece doğru commandType olan hook aktif olsun
        // Örneğin: mainPanel wire devam ettiriliyorsa, sadece DRAW_MAIN_PANEL hook aktif olmalı
        const continueFromShaftForThisHook = isContinueFromShaft && sourceCommandType === options.commandType;

        const shouldActivate = base.commandType === options.commandType || continueFromShaftForThisHook;



        if (!shouldActivate || !scene || !camera || !renderer) return;


        const domElement = renderer.domElement;
        // Named handler → cleanup'ta kaldırılabilsin (inline arrow her çalışmada
        // yeni listener bırakıyordu = sızıntı).
        const preventContextMenu = (e) => e.preventDefault();
        domElement.addEventListener('contextmenu', preventContextMenu);

        // ✅ continueFromShaftMode aktifse farklı mesaj göster
        if (continueFromShaftForThisHook) {
            base.dispatch(setCommandMessage("Kablo bacasından çıkış noktasını seçin, tıklayarak hat çizimini başlatın."));
        } else {
            base.dispatch(setCommandMessage("Cihaz yerleşimi için ilk noktayı seçin"));
        }

        const handleClick = (event) => {


            // ✅ enabled() kontrolünü kaldır - event listener zaten doğru commandType'da register edildi
            // if (!base.enabled()) return;

            const rect = domElement.getBoundingClientRect();
            const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
            const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
            base.lastMouse.current = { x, y };

            const mouseNDC = new THREE.Vector2(x, y);
            const worldPoint = new THREE.Vector3(x, y, 0).unproject(camera);

            // ✅ Kablo bacasından devam modu kontrolü
            if (continueFromShaftModeRef.current && selectedExitPointRef.current) {
                const exitPoint = selectedExitPointRef.current;
                const startPoint = new THREE.Vector3(
                    exitPoint.position.x,
                    exitPoint.position.y,
                    0
                );

                // Wire çizimini başlat
                base.startRef.current = true;
                base.verticesRef.current = [startPoint];

                // SnapSource'u kablo bacası bağlantısı olarak ayarla
                const shaftData = continueFromShaftDataRef.current;
                base.snapSourceRef.current = {
                    type: 'cableShaftConnection',
                    position: startPoint.clone(),
                    p: {
                        shaftId: shaftData?.shaftId,
                        shaftGroupId: shaftData?.shaftGroupId,
                        sourceWireId: shaftData?.sourceWireId,
                        sourceCable: shaftData?.sourceCable,
                        panelID: shaftData?.sourceCable?.sourcePanelId,
                    },
                    userData: {
                        type: 'cableShaftConnection',
                        sourceObject: shaftData?.shaftGroup
                    }
                };

                // Çıkış noktasını işaretle (occupied yap)
                exitPoint.userData.isOccupied = true;
                exitPoint.userData.isNewExitPoint = false;
                exitPoint.material.color.setHex(0xff6600);

                // Snap marker'ı gizle
                if (exitPointSnapMarkerRef.current) {
                    exitPointSnapMarkerRef.current.visible = false;
                }

                // Modu kapat
                continueFromShaftModeRef.current = false;


                // ✅ Gelen ok sembolü oluştur (hangi kattan geldiğini gösterir)
                // Kaynak katın ve mevcut katın numarasını karşılaştır
                const sourceFloorPlanId = shaftData?.sourceCable?.sourceFloorPlanId;
                const currentFloorPlanId = currentCableShaftRef.current?.userData?.floorPlanId;

                const sourceFloor = floorPlans.find(fp => fp.id === sourceFloorPlanId);
                const currentFloor = floorPlans.find(fp => fp.id === currentFloorPlanId);

                const sourceFloorNumber = sourceFloor?.number ?? 0;
                const currentFloorNumber = currentFloor?.number ?? 0;

                // Kaynak kat numarası > mevcut kat numarası ise "yukarıdan gelen"
                // Kaynak kat numarası < mevcut kat numarası ise "aşağıdan gelen"
                const incomingDirection = sourceFloorNumber > currentFloorNumber ? 'fromAbove' : 'fromBelow';



                // Gelen ok sembolünü oluştur
                const incomingArrow = createIncomingArrow(null, {
                    position: { x: startPoint.x, y: startPoint.y },
                    direction: incomingDirection,
                    color: base.wireColor || 0x00ff00,
                    isPreview: false
                });

                incomingArrow.userData = {
                    ...incomingArrow.userData,
                    type: 'incomingFloorTransitionArrow',
                    direction: incomingDirection,
                    shaftId: shaftData?.shaftId,
                    shaftGroupId: shaftData?.shaftGroupId,
                    sourceWireId: shaftData?.sourceWireId,
                    sourceFloorPlanId: sourceFloorPlanId,
                    currentFloorPlanId: currentFloorPlanId,
                    isSelectable: false,
                    isGizmo: false
                };

                scene.add(incomingArrow);

                // Çıkış noktasına ok bilgisini ekle
                exitPoint.userData.incomingArrowId = incomingArrow.userData.id;

                // Temp line oluştur
                const geometry = new THREE.BufferGeometry().setFromPoints([startPoint, startPoint.clone()]);
                const material = new THREE.LineBasicMaterial({ color: base.wireColor });
                base.tempLineRef.current = new THREE.Line(geometry, material);
                scene.add(base.tempLineRef.current);

                base.dispatch(setCommandMessage('Hat çizimi başladı. Devam etmek için tıklayın, bitirmek için Enter.'));
                return;
            }

            let dynamicSnapPoints = [...snapPoints];
            let forWhere = "panel";

            if (base.step === 0) {
                forWhere = "panel";
                scene.traverse(obj => {
                    if (obj.userData?.connectionPoints) {
                        obj.userData.connectionPoints.forEach(p => {
                            if (p.type === 'panelCon_out') {
                                const worldPos = p.position.clone().applyMatrix4(obj.matrixWorld);
                                dynamicSnapPoints.push({
                                    no: obj.uuid,
                                    position: worldPos,
                                    type: "panelConnection",
                                    connection: p
                                });
                            }
                        });
                    }
                });
            } else {
                forWhere = "draw";
            }

            // İlk vertex (hat henüz başlamadı) + priz/aydınlatma → SADECE kendi-tip
            // wire/buat/pano snap'leri kullan. Duvar vertex'i priz vertex'iyle AYNI
            // noktaya denk gelse bile snap doğru (priz/aydınlatma) kaynağa gider;
            // yanlış tipten başlatma imkânsız. Sonraki vertexlerde filtre yok (duvara
            // snaplenerek hat çizilebilsin).
            if (!base.startRef.current &&
                (options.commandType === COMMANDS.DRAW_SOCKET || options.commandType === COMMANDS.DRAW_LIGHT)) {
                dynamicSnapPoints = dynamicSnapPoints.filter(sp => isValidWireStartSource(sp, options.commandType));
            }

            let { finalPoint, snapped, snapSource } = getSnappedPointForPanel(mouseNDC, worldPoint, dynamicSnapPoints, camera, renderer, {
                snapMode: base.snapMode,
                forWhere: forWhere
            });

            // ✅ Midpoint snap kontrolü (handleClick için) - sadece Ctrl+G basılı iken
            const midpointSnap = (base.midpointSnapActiveRef?.current && base.verticesRef.current.length > 0)
                ? getMidpointSnaps(scene, camera, mouseNDC, worldPoint, 15)
                : null;
            if (midpointSnap) {
                finalPoint = midpointSnap.position;
                snapped = true;
                snapSource = midpointSnap;
            }

            if (snapSource && !base.startRef.current) {
                // Priz/aydınlatma hattı yanlış tip köşeden (duvar vb.) başlatılmasın.
                if (!isValidWireStartSource(snapSource, options.commandType)) {
                    base.dispatch(setCommandMessage(
                        options.commandType === COMMANDS.DRAW_SOCKET
                            ? 'Priz hattı yalnız mevcut priz hattı, buat veya panodan başlatılabilir.'
                            : 'Aydınlatma hattı yalnız mevcut aydınlatma hattı, buat veya panodan başlatılabilir.'
                    ));
                    return;
                }
                base.startRef.current = true;
                base.snapSourceRef.current = snapSource;
            }

            // ✅ Auto-finish devam ediyorsa veya wiring başlamamışsa işlem yapma
            if (!base.startRef.current || base.autoFinishInProgressRef.current) return;

            if (!snapped) {
                if (base.gridMode) {
                    // Relative to last vertex — from start point only multiples
                    // of gridStep away are reachable
                    const ref = base.verticesRef.current[base.verticesRef.current.length - 1];
                    finalPoint = applyRelativeGridSnap(worldPoint, ref, base.gridStep || 5);
                } else {
                    finalPoint = worldPoint;
                }
            }

            if (base.snapWall && base.offsetLineRef.current && base.verticesRef.current.length > 0) {
                event.preventDefault();
                const hasTwoLines = Array.isArray(base.offsetLineRef.current) && base.offsetLineRef.current.length === 2;
                const targetIndex = event.button === 2 && hasTwoLines ? 1 : 0;
                const targetLine = hasTwoLines
                    ? base.offsetLineRef.current[targetIndex]
                    : base.offsetLineRef.current;

                if (targetLine?.geometry?.attributes?.position) {
                    const positions = targetLine.geometry.attributes.position.array;
                    const start = new THREE.Vector3(positions[0], positions[1], positions[2]);
                    const end = new THREE.Vector3(positions[3], positions[4], positions[5]);
                    const lastVertex = base.verticesRef.current[base.verticesRef.current.length - 1];
                    
                    // Çizgi eksen hizalı mı kontrol et
                    if (isAxisAligned(start, end)) {
                        // Eksen hizalı çizgiler için
                        if (base.lastOffsetDirectionRef?.current) {
                            // Önceki açılı çizginin yönünde devam et
                            finalPoint = getDirectionalIntersection(lastVertex, base.lastOffsetDirectionRef.current, { start, end });
                        } else {
                            // Normal perpendicular intersection kullan
                            finalPoint = getPerpendicularIntersection(lastVertex, { start, end });
                        }
                        // Eksen hizalı çizgiye tıklandığında açılı yön sıfırla
                        if (base.lastOffsetDirectionRef) {
                            base.lastOffsetDirectionRef.current = null;
                        }
                    } else {
                        // Açılı çizgiler için
                        if (base.lastOffsetDirectionRef?.current) {
                            // Önceki açılı çizginin yönünde devam et
                            finalPoint = getDirectionalIntersection(lastVertex, base.lastOffsetDirectionRef.current, { start, end });
                        } else {
                            // Mouse yönünde kesişim hesapla
                            const previewEnd = base.previewEndPointRef?.current 
                                ? new THREE.Vector3(base.previewEndPointRef.current.x, base.previewEndPointRef.current.y, 0)
                                : worldPoint;
                            finalPoint = getPreviewRayIntersection(lastVertex, previewEnd, { start, end });
                        }
                        // Açılı çizginin yönünü sakla (sonraki çizim için)
                        if (base.lastOffsetDirectionRef) {
                            base.lastOffsetDirectionRef.current = getLineDirection(start, end);
                        }
                    }
                }
            } else if (!snapped) {
                if (base.gridMode) {
                    const ref2 = base.verticesRef.current[base.verticesRef.current.length - 1];
                    finalPoint = applyRelativeGridSnap(worldPoint, ref2, base.gridStep || 5);
                } else {
                    finalPoint = worldPoint;
                }
            }

            // Panel çıkışı için dik çıkış kuralı - SADECE İLK KENAR İÇİN (mainPanel için devre dışı)
            if (!base.disablePerpendicularExit && base.verticesRef.current.length === 1 && base.snapSourceRef.current?.type === 'panelConnection') {
                const sourcePanel = base.snapSourceRef.current.userData?.sourceObject;
                if (sourcePanel) {
                    const exitPoint = base.verticesRef.current[0];
                    finalPoint = calculatePerpendicularExitPoint(exitPoint, finalPoint, sourcePanel);
                }
            } else if (base.orthoMode && base.verticesRef.current.length > 0) {
                const lastVertex = base.verticesRef.current[base.verticesRef.current.length - 1];
                finalPoint = getOrthoSnappedPoint(lastVertex, finalPoint);
            }

            // Snap marker'ı temizle ve yenisini oluştur
            if (base.snapMarkerRef.current) {
                scene.remove(base.snapMarkerRef.current);
                base.snapMarkerRef.current.geometry.dispose();
                base.snapMarkerRef.current.material.dispose();
                base.snapMarkerRef.current = null;
            }

            if (snapped && snapSource) {
                const marker = createSnapMarker(8, snapSource);
                marker.position.copy(finalPoint);
                scene.add(marker);
                base.snapMarkerRef.current = marker;
            }

            const isLeftClick = event.button === 0;
            const isRightClick = event.button === 2;
            const hasWallGuideLines = base.snapWall && base.offsetLineRef.current && base.verticesRef.current.length > 0;

            // ── Trace Follow commit ───────────────────────────────────────────────
            // Ctrl+Shift tıklamasında trace önizleme noktaları vertex olarak eklenir.
            // İlk trace noktası başlangıç vertex'iyle çakışırsa atla — yoksa wire
            // başlangıcında üst üste vertex (sıfır-uzunluklu kenar) oluşur.
            if (isLeftClick && base.ctrlPressedRef.current && shiftPressedRef.current &&
                base.startRef.current && tracePreviewPtsRef.current.length > 0) {
                const tracePts = tracePreviewPtsRef.current;
                for (const pt of tracePts) {
                    const cloned = pt.clone();
                    const last = base.verticesRef.current[base.verticesRef.current.length - 1];
                    if (last && Math.abs(last.x - cloned.x) < 0.01 && Math.abs(last.y - cloned.y) < 0.01) {
                        continue; // duplicate consecutive vertex'i atla
                    }
                    const vcb = base.verticesRef.current.length;
                    base.verticesRef.current.push(cloned);
                    base.undoStackRef.current.push({ type: 'vertex', vertex: cloned, vertexCountBefore: vcb });
                }
                base.redoStackRef.current = [];
                tracePreviewPtsRef.current = [];
                traceGuideSideRef.current = 0; // Sonraki trace için tarafı sıfırla
                traceGuideRef.current = null;
                base.updateTempLine();
                return;
            }

            if (isLeftClick || (isRightClick && hasWallGuideLines)) {
                // ✅ Vertex sayısını takip et (backward offset 2 vertex ekleyebilir)
                const vertexCountBefore = base.verticesRef.current.length;

                // Use base helper to get backward-offset insertion logic
                base.pushOrUpdateVertex(finalPoint, worldPoint);

                const vertexCountAfter = base.verticesRef.current.length;
                const verticesAdded = vertexCountAfter - vertexCountBefore;

                // ✅ Eklenen TÜM vertex'leri undo stack'e ekle
                // (pushOrUpdateVertex artık her zaman append yapıyor, replace yok)
                if (verticesAdded > 0 && base.verticesRef.current.length > 1) {
                    for (let i = 0; i < verticesAdded; i++) {
                        const vertexIndex = vertexCountAfter - verticesAdded + i;
                        base.undoStackRef.current.push({
                            type: 'vertex',
                            vertex: base.verticesRef.current[vertexIndex].clone(),
                            vertexCountBefore: vertexCountBefore + i // ✅ Her vertex için doğru snapshot
                        });
                    }
                    base.redoStackRef.current = [];
                }

                if (base.verticesRef.current.length === 1 && base.onWireStart) {
                    base.onWireStart(base.verticesRef.current[0]);
                }

                if (base.verticesRef.current.length > 1) {
                    base.updateTempLine();
                }

                base.finalPointRef.current = finalPoint;
                base.dispatch(setCommandMessage("Sonraki noktaları seçin. Bitirmek için Enter/Space, iptal için ESC."));
            }
        };

        const handleMouseMove = (event) => {
            // ✅ enabled() kontrolünü kaldır - event listener zaten doğru commandType'da register edildi
            // if (!base.enabled()) return;

            // ✅ Auto-finish devam ediyorsa işlem yapma
            if (base.autoFinishInProgressRef.current) return;

            const rect = domElement.getBoundingClientRect();
            const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
            const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
            base.lastMouse.current = { x, y };
            const mouseNDC = new THREE.Vector2(x, y);
            const worldPoint = new THREE.Vector3(x, y, 0).unproject(camera);

            // ✅ CONTINUE FROM SHAFT MODE - Mouse hareket ettiğinde çıkış noktası seçimi
            if (continueFromShaftModeRef.current && currentCableShaftRef.current) {
                const shaftGroup = currentCableShaftRef.current;

                // En yakın çıkış noktasını bul
                let closestExitPoint = null;
                let closestDistance = Infinity;

                shaftGroup.traverse((child) => {
                    if (child.userData?.type === 'shaftExitPoint') {
                        const dx = worldPoint.x - child.position.x;
                        const dy = worldPoint.y - child.position.y;
                        const distance = Math.sqrt(dx * dx + dy * dy);
                        if (distance < closestDistance) {
                            closestDistance = distance;
                            closestExitPoint = child;
                        }
                    }
                });

                // Seçili çıkış noktasını güncelle
                if (closestExitPoint && closestExitPoint !== selectedExitPointRef.current) {
                    selectedExitPointRef.current = closestExitPoint;

                    // Snap marker'ı güncelle
                    if (exitPointSnapMarkerRef.current) {
                        exitPointSnapMarkerRef.current.position.set(
                            closestExitPoint.position.x,
                            closestExitPoint.position.y,
                            0.25
                        );
                    }

                }

                return; // Diğer işlemlere geçme
            }

            // ── Trace Follow (Ctrl+Shift) ─────────────────────────────────────────
            // Çizim başlamışsa ve Ctrl+Shift tutuluyorsa, mouse'a en yakın mevcut
            // wire bulunur; son vertex'ten mouse projeksiyonuna kadar olan güzergâh
            // 5 birim ofsetle önizleme olarak gösterilir.
            if (base.ctrlPressedRef.current && shiftPressedRef.current &&
                base.startRef.current && base.verticesRef.current.length > 0) {
                const tempName = base.tempLineRef.current?.name ?? '';
                const guide = findGuideWireAtPoint(scene, worldPoint, tempName);
                if (guide) {
                    // Tarafı yalnızca klavuz wire değiştiğinde yeniden hesapla
                    if (!traceGuideSideRef.current || traceGuideRef.current?.wire !== guide.wire) {
                        const lastPt = base.verticesRef.current[base.verticesRef.current.length - 1];
                        traceGuideSideRef.current = getSideOfWire(guide.positions, lastPt);
                        traceGuideRef.current = guide;
                    }
                    const lastPt = base.verticesRef.current[base.verticesRef.current.length - 1];
                    const fromT  = projectOntoWire(guide.positions, lastPt);
                    const tracePts = computeOffsetTracePath(
                        guide.positions, fromT, guide.projT, 5, traceGuideSideRef.current
                    );
                    tracePreviewPtsRef.current = tracePts;

                    // Component placement için previewEndPointRef'i son trace noktasına set et
                    if (tracePts.length > 0) {
                        base.previewEndPointRef.current = tracePts[tracePts.length - 1].clone();
                    }

                    // tempLine'ı doğrudan güncelle
                    const allPts = [...base.verticesRef.current, ...tracePts];
                    if (allPts.length >= 2) {
                        if (base.tempLineRef.current) {
                            const geom = new THREE.BufferGeometry().setFromPoints(allPts);
                            base.tempLineRef.current.geometry.dispose();
                            base.tempLineRef.current.geometry = geom;
                        } else {
                            const geom = new THREE.BufferGeometry().setFromPoints(allPts);
                            const mat  = new THREE.LineBasicMaterial({ color: base.wireColor, linewidth: 4, depthTest: false });
                            base.tempLineRef.current = new THREE.Line(geom, mat);
                            base.tempLineRef.current.name = `temp${base.itemTypeName ?? ''}WirePreview`;
                            scene.add(base.tempLineRef.current);
                        }
                    }
                    return; // Normal önizlemeyi atla
                }
            }
            // Trace modu aktif değilse önizleme noktalarını temizle
            if (tracePreviewPtsRef.current.length > 0) {
                tracePreviewPtsRef.current = [];
            }

            let dynamicSnapPoints = [...snapPoints];

            // İlk vertex ÖNİZLEMESİ + priz/aydınlatma → sadece kendi-tip snap'ler
            // (click filtresiyle tutarlı; duvar vertex'inde önizleme snap'i oluşmasın).
            if (!base.startRef.current &&
                (options.commandType === COMMANDS.DRAW_SOCKET || options.commandType === COMMANDS.DRAW_LIGHT)) {
                dynamicSnapPoints = dynamicSnapPoints.filter(sp => isValidWireStartSource(sp, options.commandType));
            }

            const { finalPoint, snapped, snapSource } = getSnappedPoint(mouseNDC, worldPoint, dynamicSnapPoints, camera, renderer, { snapMode: base.snapMode });

            // ✅ Midpoint snap kontrolü (duvar çizgilerinin orta noktaları) - sadece Ctrl+G basılı iken
            const midpointSnap = (base.midpointSnapActiveRef?.current && base.verticesRef.current.length > 0)
                ? getMidpointSnaps(scene, camera, mouseNDC, worldPoint, 15)
                : null;
            
            if (base.snapMarkerRef.current) {
                scene.remove(base.snapMarkerRef.current);
                base.snapMarkerRef.current.geometry.dispose();
                base.snapMarkerRef.current.material.dispose();
                base.snapMarkerRef.current = null;
            }

            // Önce midpoint snap'i kontrol et (öncelikli)
            let effectiveSnapSource = snapSource;
            let effectiveFinalPoint = finalPoint;
            
            if (midpointSnap) {
                const marker = createSnapMarker(10, { type: 'midpoint' });
                marker.position.copy(midpointSnap.position);
                scene.add(marker);
                base.snapMarkerRef.current = marker;
                effectiveSnapSource = midpointSnap;
                effectiveFinalPoint = midpointSnap.position;
            } else if (snapped && snapSource) {
                const marker = createSnapMarker(8, snapSource);
                marker.position.copy(snapSource.position);
                scene.add(marker);
                base.snapMarkerRef.current = marker;
            }

            let previewPoint = effectiveFinalPoint;

            // Midpoint snap yoksa diğer kontrolleri yap
            if (!midpointSnap) {
                // Panel çıkışı için dik çıkış kuralı (mainPanel için devre dışı)
                if (!base.disablePerpendicularExit && base.verticesRef.current.length === 1 && base.snapSourceRef.current?.type === 'panelConnection') {
                    const sourcePanel = base.snapSourceRef.current.userData?.sourceObject;
                    if (sourcePanel) {
                        const exitPoint = base.verticesRef.current[0];
                        previewPoint = calculatePerpendicularExitPoint(exitPoint, effectiveFinalPoint, sourcePanel);
                    }
                } else if (base.orthoMode && base.verticesRef.current.length > 0) {
                    const lastVertex = base.verticesRef.current[base.verticesRef.current.length - 1];
                    previewPoint = getOrthoSnappedPoint(lastVertex, effectiveFinalPoint);
                }
            }

            // ─── F9 Grid snap: mouse serbestçe hareket eder ama previewPoint
            //    son vertex'e göre 5 birim kademeye oturur.
            //    Snap hit (endpoint/intersection) veya midpoint snap varsa o noktayı bozmayız.
            if (base.gridMode && !snapped && !midpointSnap && base.verticesRef.current.length > 0) {
                const ref = base.verticesRef.current[base.verticesRef.current.length - 1];
                previewPoint = applyRelativeGridSnap(previewPoint, ref, base.gridStep || 5);
            }

            if (base.verticesRef.current.length > 0) {
                // Use base helper to enable backward-offset preview
                base.updateTempLine(previewPoint, worldPoint);

                WireMarkerGenerator.generateMarkersForSegments(
                    base.verticesRef.current,
                    base.previewMarkersRef.current,
                    base.wireMarkersRef.current,
                    scene,
                    null,
                    options.markerCount ?? 3,
                    options.simplifyCollinearVertices === false, // ✅ spline → eğri-bazlı marker (wire üzerinde kalsın)
                    options.markerSkipFirstSegment === true // ✅ preview = permanent (ilk edge atla)
                );
            }

            if (base.previewSymbolRef.current) {
                { const _ps = base.previewSymbolRef.current; if (_ps.parent) _ps.parent.remove(_ps); else scene.remove(_ps); }
                base.previewSymbolRef.current.traverse(child => {
                    if (child.geometry) child.geometry.dispose();
                    if (child.material) child.material.dispose();
                });
                base.previewSymbolRef.current = null;
            }

            if (base.verticesRef.current.length > 0 && previewPoint) {
                const lastPoint = base.verticesRef.current[base.verticesRef.current.length - 1];
                const directionVector = new THREE.Vector3().subVectors(previewPoint, lastPoint).normalize();
                const angle = Math.atan2(directionVector.y, directionVector.x);

                const symbolPreview = base.createSymbol(previewPoint, directionVector, angle, true, base.itemTypeRef.current, { rotateValue: rotateOffsetRef.current });
                // ⚠️ Parent group rotation'ı override etme - createSymbol zaten offsetGroup rotation'ı doğru set ediyor
                // rotateOffset (kullanıcının 'r' ile eklediği rotasyon) createSymbol içinde uygulanıyor
                // symbolPreview.rotation.z = angle;

                // ✅ Ok ön izlemesi aktifse component'i transparan yap
                if (arrowPreviewActiveRef.current) {
                    symbolPreview.traverse(child => {
                        if (child.material) {
                            child.material.transparent = true;
                            child.material.opacity = 0.3;
                            child.material.needsUpdate = true;
                        }
                    });
                }

                scene.add(symbolPreview);
                base.previewSymbolRef.current = symbolPreview;
            }

            // ✅ KABLO BACASI KONTROLÜ - Wire çizimi sırasında mouse kablo bacası içine girerse ok göster
            if (base.verticesRef.current.length > 0 && previewPoint) {
                const nearestShaft = findNearestCableShaft(scene, { x: previewPoint.x, y: previewPoint.y }, 0);

                if (nearestShaft) {
                    // Mouse kablo bacası içinde
                    if (!isInsideCableShaftRef.current) {
                        // Yeni giriş - ilk kez kablo bacasına girdi
                        isInsideCableShaftRef.current = true;
                        currentCableShaftRef.current = nearestShaft;

                        // ✅ Çıkış noktası oluştur/güncelle (önizleme için)
                        const shaftData = nearestShaft.userData;
                        if (shaftData && shaftData.diagonalStart && shaftData.diagonalEnd) {
                            const shaftId = shaftData.id;
                            const occupiedCount = shaftData.occupiedExitCount || 0;
                            const newExitCount = occupiedCount + 1; // Yeni kablo için +1

                            // Mevcut çıkış noktalarını temizle ve wire bilgilerini sakla
                            const existingExitPoints = [];
                            const occupiedExitInfo = [];
                            nearestShaft.traverse((child) => {
                                if (child.userData?.type === 'shaftExitPoint') {
                                    existingExitPoints.push(child);
                                    if (child.userData.isOccupied && child.userData.wireId) {
                                        occupiedExitInfo.push({
                                            exitIndex: child.userData.exitIndex,
                                            wireId: child.userData.wireId,
                                            direction: child.userData.direction || 'in'
                                        });
                                    }
                                }
                            });
                            existingExitPoints.forEach(point => {
                                nearestShaft.remove(point);
                                point.geometry?.dispose();
                                point.material?.dispose();
                            });

                            // Yeni çıkış noktaları oluştur
                            for (let i = 0; i < newExitCount; i++) {
                                const t = (i + 1) / (newExitCount + 1);
                                const position = getPointOnDiagonal(shaftData.diagonalStart, shaftData.diagonalEnd, t);

                                const isOccupied = i < occupiedCount;
                                const wireInfo = occupiedExitInfo[i] || null;

                                const circleGeometry = new THREE.CircleGeometry(2, 16);
                                const circleMaterial = new THREE.MeshBasicMaterial({
                                    color: isOccupied ? 0xff6600 : 0x00ff00,
                                    side: THREE.DoubleSide,
                                    transparent: true,
                                    opacity: 0.8
                                });

                                const circle = new THREE.Mesh(circleGeometry, circleMaterial);
                                circle.position.copy(position);
                                circle.name = `shaft-exit-point-${i}`;
                                circle.userData = {
                                    type: 'shaftExitPoint',
                                    shaftId: shaftId,
                                    exitIndex: i,
                                    isExitPoint: true,
                                    isOccupied: isOccupied,
                                    wireId: wireInfo?.wireId || null,
                                    direction: wireInfo?.direction || null,
                                    isShaftPart: true
                                };

                                nearestShaft.add(circle);

                                // ✅ Mevcut wire'ların vertex'lerini ve arrow'larını güncelle
                                if (isOccupied && wireInfo?.wireId) {
                                    // Önce wire'ın ESKİ son vertex pozisyonunu bul (arrow eşleştirmesi için)
                                    let oldLastX = null, oldLastY = null;
                                    scene.traverse((obj) => {
                                        if (obj.userData?.id === wireInfo.wireId && obj.geometry?.attributes?.position) {
                                            const posAttr = obj.geometry.attributes.position;
                                            const numVerts = posAttr.count;
                                            if (numVerts > 0) {
                                                oldLastX = posAttr.getX(numVerts - 1);
                                                oldLastY = posAttr.getY(numVerts - 1);
                                            }
                                        }
                                    });

                                    // Wire vertex'lerini güncelle - b vertex'ini K kenarının yönünü koruyarak ötele
                                    scene.traverse((obj) => {
                                        if (obj.userData?.id === wireInfo.wireId && obj.geometry?.attributes?.position) {
                                            const posAttr = obj.geometry.attributes.position;
                                            const numVerts = posAttr.count;

                                            if (numVerts >= 2) {
                                                const cIdx = numVerts - 1;
                                                const bIdx = numVerts - 2;

                                                const cX = position.x;
                                                const cY = position.y;
                                                const bOldX = posAttr.getX(bIdx);
                                                const bOldY = posAttr.getY(bIdx);

                                                let newBx, newBy;

                                                if (numVerts >= 3) {
                                                    const aIdx = numVerts - 3;
                                                    const aX = posAttr.getX(aIdx);
                                                    const aY = posAttr.getY(aIdx);

                                                    const kDirX = bOldX - aX;
                                                    const kDirY = bOldY - aY;

                                                    if (Math.abs(kDirX) > Math.abs(kDirY)) {
                                                        newBx = cX;
                                                        newBy = bOldY;
                                                    } else {
                                                        newBx = bOldX;
                                                        newBy = cY;
                                                    }
                                                } else {
                                                    const dirX = cX - bOldX;
                                                    const dirY = cY - bOldY;

                                                    if (Math.abs(dirX) > Math.abs(dirY)) {
                                                        newBx = cX;
                                                        newBy = bOldY;
                                                    } else {
                                                        newBx = bOldX;
                                                        newBy = cY;
                                                    }
                                                }

                                                posAttr.setXYZ(bIdx, newBx, newBy, 0);
                                                posAttr.setXYZ(cIdx, cX, cY, 0);
                                                posAttr.needsUpdate = true;
                                                obj.geometry.computeBoundingSphere();
                                            } else if (numVerts === 1) {
                                                posAttr.setXYZ(0, position.x, position.y, 0);
                                                posAttr.needsUpdate = true;
                                                obj.geometry.computeBoundingSphere();
                                            }
                                        }
                                    });

                                    // Arrow'ları güncelle (ESKİ wire pozisyonuna yakın olanları)
                                    if (oldLastX !== null && oldLastY !== null) {
                                        scene.traverse((obj) => {
                                            if (obj.userData?.type === 'floorTransitionArrow') {
                                                const arrowPos = obj.position;
                                                const dx = arrowPos.x - oldLastX;
                                                const dy = arrowPos.y - oldLastY;
                                                if (Math.sqrt(dx*dx + dy*dy) < 5) {
                                                    obj.position.set(position.x, position.y, obj.position.z);
                                                }
                                            }
                                        });
                                    }
                                }
                            }

                            shaftData.exitCount = newExitCount;

                            // ✅ Çıkış noktaları oluşturulduktan HEMEN SONRA snap kontrolü yap
                            let closestExitPointInitial = null;
                            let closestDistanceInitial = Infinity; // Threshold yok - en yakını bul
                            nearestShaft.traverse((child) => {
                                if (child.userData?.type === 'shaftExitPoint') {
                                    const dx = previewPoint.x - child.position.x;
                                    const dy = previewPoint.y - child.position.y;
                                    const distance = Math.sqrt(dx * dx + dy * dy);
                                    if (distance < closestDistanceInitial) {
                                        closestDistanceInitial = distance;
                                        closestExitPointInitial = child;
                                    }
                                }
                            });

                            if (closestExitPointInitial) {
                                selectedExitPointRef.current = closestExitPointInitial;
                                if (!exitPointSnapMarkerRef.current) {
                                    const markerGeometry = new THREE.RingGeometry(4, 6, 24);
                                    const markerMaterial = new THREE.MeshBasicMaterial({
                                        color: 0xffff00,
                                        side: THREE.DoubleSide,
                                        transparent: true,
                                        opacity: 0.9
                                    });
                                    exitPointSnapMarkerRef.current = new THREE.Mesh(markerGeometry, markerMaterial);
                                    exitPointSnapMarkerRef.current.userData = { type: 'exitPointSnapMarker' };
                                    scene.add(exitPointSnapMarkerRef.current);
                                }
                                exitPointSnapMarkerRef.current.position.set(
                                    closestExitPointInitial.position.x,
                                    closestExitPointInitial.position.y,
                                    0.25
                                );
                                exitPointSnapMarkerRef.current.visible = true;
                            }
                        }

                        // Otomatik olarak ok göster (varsayılan yukarı ok - 60 derece)
                        if (!arrowPreviewActiveRef.current) {
                            // Mevcut ok preview varsa temizle
                            if (arrowPreviewRef.current) {
                                scene.remove(arrowPreviewRef.current);
                                arrowPreviewRef.current.traverse(child => {
                                    if (child.geometry) child.geometry.dispose();
                                    if (child.material) child.material.dispose();
                                });
                                arrowPreviewRef.current = null;
                            }

                            // Ok sembolü oluştur (varsayılan yukarı - 60 derece)
                            const arrowColor = base.wireColor || 0x0045ff;
                            const arrowGroup = createArrow(scene, {
                                position: { x: previewPoint.x, y: previewPoint.y },
                                rotation: 60, // Varsayılan yukarı ok
                                color: arrowColor,
                                layerName: '0',
                                isPreview: true
                            });

                            arrowPreviewRef.current = arrowGroup;
                            arrowPreviewActiveRef.current = true;
                            arrowPreviewRotationRef.current = 60;
                        }
                    }

                    // Kablo bacası içindeyken component preview'ı gizle, ok göster
                    if (base.previewSymbolRef.current) {
                        base.previewSymbolRef.current.visible = false;
                    }

                    // ✅ DİNAMİK ÇIKIŞ NOKTASI ÖTELEME SİSTEMİ
                    // Mouse pozisyonuna göre boş slot'un yerini hesapla ve kabloları ötele
                    const shaftData = nearestShaft.userData;
                    if (shaftData && shaftData.diagonalStart && shaftData.diagonalEnd) {
                        const diagonalStart = shaftData.diagonalStart;
                        const diagonalEnd = shaftData.diagonalEnd;
                        const occupiedCount = shaftData.occupiedExitCount || 0;
                        const totalSlots = occupiedCount + 1;

                        // Çapraz çizgi üzerinde mouse'un t parametresini hesapla (0-1 arası)
                        // ✅ previewPoint (kablo ucu) yerine worldPoint (gerçek mouse pozisyonu) kullan
                        const lineVec = {
                            x: diagonalEnd.x - diagonalStart.x,
                            y: diagonalEnd.y - diagonalStart.y
                        };
                        const lineLengthSq = lineVec.x * lineVec.x + lineVec.y * lineVec.y;
                        const mouseVec = {
                            x: worldPoint.x - diagonalStart.x,
                            y: worldPoint.y - diagonalStart.y
                        };
                        const t = (mouseVec.x * lineVec.x + mouseVec.y * lineVec.y) / lineLengthSq;
                        const tClamped = Math.max(0, Math.min(1, t));

                        // Hangi slot'a yakın olduğunu hesapla
                        // Slot pozisyonları: (i+1)/(totalSlots+1) for i=0 to totalSlots-1
                        let emptySlotIndex = 0;
                        let minDist = Infinity;
                        for (let i = 0; i < totalSlots; i++) {
                            const slotT = (i + 1) / (totalSlots + 1);
                            const dist = Math.abs(tClamped - slotT);
                            if (dist < minDist) {
                                minDist = dist;
                                emptySlotIndex = i;
                            }
                        }

                        // Mevcut çıkış noktalarını ve wire bilgilerini al
                        const existingExitPoints = [];
                        const occupiedWireInfos = [];
                        nearestShaft.traverse((child) => {
                            if (child.userData?.type === 'shaftExitPoint') {
                                existingExitPoints.push(child);
                                if (child.userData.isOccupied && child.userData.wireId) {
                                    // Wire bilgilerini orijinal sıraya göre sakla
                                    occupiedWireInfos.push({
                                        wireId: child.userData.wireId,
                                        direction: child.userData.direction || 'in'
                                    });
                                }
                            }
                        });

                        // occupiedWireInfos'u sırala (exitIndex'e göre)
                        occupiedWireInfos.sort((a, b) => {
                            const aPoint = existingExitPoints.find(p => p.userData.wireId === a.wireId);
                            const bPoint = existingExitPoints.find(p => p.userData.wireId === b.wireId);
                            return (aPoint?.userData.exitIndex || 0) - (bPoint?.userData.exitIndex || 0);
                        });

                        // Çıkış noktalarını temizle
                        existingExitPoints.forEach(point => {
                            nearestShaft.remove(point);
                            point.geometry?.dispose();
                            point.material?.dispose();
                        });

                        // Yeni çıkış noktalarını oluştur (emptySlotIndex'e göre ötelenmiş)
                        let emptyExitPoint = null;
                        for (let i = 0; i < totalSlots; i++) {
                            const slotT = (i + 1) / (totalSlots + 1);
                            const position = getPointOnDiagonal(diagonalStart, diagonalEnd, slotT);

                            const isEmptySlot = (i === emptySlotIndex);

                            // Occupied wire index hesapla
                            let occupiedWireIndex = -1;
                            if (!isEmptySlot) {
                                occupiedWireIndex = i < emptySlotIndex ? i : i - 1;
                            }
                            const wireInfo = isEmptySlot ? null : occupiedWireInfos[occupiedWireIndex];

                            const circleGeometry = new THREE.CircleGeometry(2, 16);
                            const circleMaterial = new THREE.MeshBasicMaterial({
                                color: isEmptySlot ? 0x00ff00 : 0xff6600,
                                side: THREE.DoubleSide,
                                transparent: true,
                                opacity: 0.8
                            });

                            const circle = new THREE.Mesh(circleGeometry, circleMaterial);
                            circle.position.copy(position);
                            circle.name = `shaft-exit-point-${i}`;
                            circle.userData = {
                                type: 'shaftExitPoint',
                                shaftId: shaftData.id,
                                exitIndex: i,
                                isExitPoint: true,
                                isOccupied: !isEmptySlot,
                                wireId: wireInfo?.wireId || null,
                                direction: wireInfo?.direction || null,
                                isShaftPart: true
                            };

                            nearestShaft.add(circle);

                            if (isEmptySlot) {
                                emptyExitPoint = circle;
                            }

                            // ✅ Occupied wire'ların vertex'lerini ve arrow'larını güncelle
                            // b vertex'ini K kenarının yönünü koruyarak ötele
                            if (!isEmptySlot && wireInfo?.wireId) {
                                // Önce wire'ın ESKİ son vertex pozisyonunu bul (arrow eşleştirmesi için)
                                let oldLastX = null, oldLastY = null;
                                scene.traverse((obj) => {
                                    if (obj.userData?.id === wireInfo.wireId && obj.geometry?.attributes?.position) {
                                        const posAttr = obj.geometry.attributes.position;
                                        const numVerts = posAttr.count;
                                        if (numVerts > 0) {
                                            oldLastX = posAttr.getX(numVerts - 1);
                                            oldLastY = posAttr.getY(numVerts - 1);
                                        }
                                    }
                                });

                                // Wire vertex'lerini güncelle
                                scene.traverse((obj) => {
                                    if (obj.userData?.id === wireInfo.wireId && obj.geometry?.attributes?.position) {
                                        const posAttr = obj.geometry.attributes.position;
                                        const numVerts = posAttr.count;

                                        if (numVerts >= 2) {
                                            const cIdx = numVerts - 1;
                                            const bIdx = numVerts - 2;

                                            const cX = position.x;
                                            const cY = position.y;
                                            const bOldX = posAttr.getX(bIdx);
                                            const bOldY = posAttr.getY(bIdx);

                                            let newBx, newBy;

                                            if (numVerts >= 3) {
                                                const aIdx = numVerts - 3;
                                                const aX = posAttr.getX(aIdx);
                                                const aY = posAttr.getY(aIdx);

                                                const kDirX = bOldX - aX;
                                                const kDirY = bOldY - aY;

                                                if (Math.abs(kDirX) > Math.abs(kDirY)) {
                                                    newBx = cX;
                                                    newBy = bOldY;
                                                } else {
                                                    newBx = bOldX;
                                                    newBy = cY;
                                                }
                                            } else {
                                                const dirX = cX - bOldX;
                                                const dirY = cY - bOldY;

                                                if (Math.abs(dirX) > Math.abs(dirY)) {
                                                    newBx = cX;
                                                    newBy = bOldY;
                                                } else {
                                                    newBx = bOldX;
                                                    newBy = cY;
                                                }
                                            }

                                            posAttr.setXYZ(bIdx, newBx, newBy, 0);
                                            posAttr.setXYZ(cIdx, cX, cY, 0);
                                            posAttr.needsUpdate = true;
                                            obj.geometry.computeBoundingSphere();
                                        } else if (numVerts === 1) {
                                            posAttr.setXYZ(0, position.x, position.y, 0);
                                            posAttr.needsUpdate = true;
                                            obj.geometry.computeBoundingSphere();
                                        }
                                    }
                                });

                                // Arrow'ları güncelle (ESKİ wire pozisyonuna yakın olanları)
                                if (oldLastX !== null && oldLastY !== null) {
                                    scene.traverse((obj) => {
                                        if (obj.userData?.type === 'floorTransitionArrow') {
                                            const arrowPos = obj.position;
                                            const dx = arrowPos.x - oldLastX;
                                            const dy = arrowPos.y - oldLastY;
                                            if (Math.sqrt(dx*dx + dy*dy) < 5) {
                                                obj.position.set(position.x, position.y, obj.position.z);
                                            }
                                        }
                                    });
                                }
                            }
                        }

                        // Snap marker ve seçili nokta
                        if (emptyExitPoint) {
                            selectedExitPointRef.current = emptyExitPoint;

                            if (!exitPointSnapMarkerRef.current) {
                                const markerGeometry = new THREE.RingGeometry(4, 6, 24);
                                const markerMaterial = new THREE.MeshBasicMaterial({
                                    color: 0xffff00,
                                    side: THREE.DoubleSide,
                                    transparent: true,
                                    opacity: 0.9
                                });
                                exitPointSnapMarkerRef.current = new THREE.Mesh(markerGeometry, markerMaterial);
                                exitPointSnapMarkerRef.current.userData = { type: 'exitPointSnapMarker' };
                                scene.add(exitPointSnapMarkerRef.current);
                            }

                            exitPointSnapMarkerRef.current.position.set(
                                emptyExitPoint.position.x,
                                emptyExitPoint.position.y,
                                0.25
                            );
                            exitPointSnapMarkerRef.current.visible = true;

                            if (arrowPreviewRef.current) {
                                arrowPreviewRef.current.position.set(
                                    emptyExitPoint.position.x,
                                    emptyExitPoint.position.y,
                                    0
                                );
                            }
                        }
                    }

                } else {
                    // Mouse kablo bacası dışında
                    if (isInsideCableShaftRef.current) {
                        // ✅ Çıkış noktalarını geri al (önizleme iptal)
                        const prevShaft = currentCableShaftRef.current;
                        if (prevShaft && prevShaft.userData) {
                            const shaftData = prevShaft.userData;
                            const occupiedCount = shaftData.occupiedExitCount || 0;

                            // Mevcut çıkış noktalarını temizle ve wire bilgilerini sakla
                            const existingExitPoints = [];
                            const occupiedExitInfo = [];
                            prevShaft.traverse((child) => {
                                if (child.userData?.type === 'shaftExitPoint') {
                                    existingExitPoints.push(child);
                                    if (child.userData.isOccupied && child.userData.wireId) {
                                        occupiedExitInfo.push({
                                            exitIndex: child.userData.exitIndex,
                                            wireId: child.userData.wireId,
                                            direction: child.userData.direction || 'in'
                                        });
                                    }
                                }
                            });
                            existingExitPoints.forEach(point => {
                                prevShaft.remove(point);
                                point.geometry?.dispose();
                                point.material?.dispose();
                            });

                            // Sadece dolu olanları geri oluştur
                            if (occupiedCount > 0 && shaftData.diagonalStart && shaftData.diagonalEnd) {
                                for (let i = 0; i < occupiedCount; i++) {
                                    const t = (i + 1) / (occupiedCount + 1);
                                    const position = getPointOnDiagonal(shaftData.diagonalStart, shaftData.diagonalEnd, t);
                                    const wireInfo = occupiedExitInfo[i] || null;

                                    const circleGeometry = new THREE.CircleGeometry(2, 16);
                                    const circleMaterial = new THREE.MeshBasicMaterial({
                                        color: 0xff6600,
                                        side: THREE.DoubleSide,
                                        transparent: true,
                                        opacity: 0.8
                                    });

                                    const circle = new THREE.Mesh(circleGeometry, circleMaterial);
                                    circle.position.copy(position);
                                    circle.name = `shaft-exit-point-${i}`;
                                    circle.userData = {
                                        type: 'shaftExitPoint',
                                        shaftId: shaftData.id,
                                        exitIndex: i,
                                        isExitPoint: true,
                                        isOccupied: true,
                                        wireId: wireInfo?.wireId || null,
                                        direction: wireInfo?.direction || null,
                                        isShaftPart: true
                                    };

                                    prevShaft.add(circle);

                                    // ✅ Wire vertex'lerini ve arrow'ları orijinal pozisyonlara geri al
                                    if (wireInfo?.wireId) {
                                        // Önce wire'ın ESKİ son vertex pozisyonunu bul (arrow eşleştirmesi için)
                                        let oldLastX = null, oldLastY = null;
                                        scene.traverse((obj) => {
                                            if (obj.userData?.id === wireInfo.wireId && obj.geometry?.attributes?.position) {
                                                const posAttr = obj.geometry.attributes.position;
                                                const numVerts = posAttr.count;
                                                if (numVerts > 0) {
                                                    oldLastX = posAttr.getX(numVerts - 1);
                                                    oldLastY = posAttr.getY(numVerts - 1);
                                                }
                                            }
                                        });

                                        // Wire vertex'lerini güncelle - b vertex'ini K kenarının yönünü koruyarak ötele
                                        scene.traverse((obj) => {
                                            if (obj.userData?.id === wireInfo.wireId && obj.geometry?.attributes?.position) {
                                                const posAttr = obj.geometry.attributes.position;
                                                const numVerts = posAttr.count;

                                                if (numVerts >= 2) {
                                                    const cIdx = numVerts - 1;
                                                    const bIdx = numVerts - 2;

                                                    const cX = position.x;
                                                    const cY = position.y;
                                                    const bOldX = posAttr.getX(bIdx);
                                                    const bOldY = posAttr.getY(bIdx);

                                                    let newBx, newBy;

                                                    if (numVerts >= 3) {
                                                        const aIdx = numVerts - 3;
                                                        const aX = posAttr.getX(aIdx);
                                                        const aY = posAttr.getY(aIdx);

                                                        const kDirX = bOldX - aX;
                                                        const kDirY = bOldY - aY;

                                                        if (Math.abs(kDirX) > Math.abs(kDirY)) {
                                                            newBx = cX;
                                                            newBy = bOldY;
                                                        } else {
                                                            newBx = bOldX;
                                                            newBy = cY;
                                                        }
                                                    } else {
                                                        const dirX = cX - bOldX;
                                                        const dirY = cY - bOldY;

                                                        if (Math.abs(dirX) > Math.abs(dirY)) {
                                                            newBx = cX;
                                                            newBy = bOldY;
                                                        } else {
                                                            newBx = bOldX;
                                                            newBy = cY;
                                                        }
                                                    }

                                                    posAttr.setXYZ(bIdx, newBx, newBy, 0);
                                                    posAttr.setXYZ(cIdx, cX, cY, 0);
                                                    posAttr.needsUpdate = true;
                                                    obj.geometry.computeBoundingSphere();
                                                } else if (numVerts === 1) {
                                                    posAttr.setXYZ(0, position.x, position.y, 0);
                                                    posAttr.needsUpdate = true;
                                                    obj.geometry.computeBoundingSphere();
                                                }
                                            }
                                        });

                                        // Arrow'ları güncelle (ESKİ wire pozisyonuna yakın olanları)
                                        if (oldLastX !== null && oldLastY !== null) {
                                            scene.traverse((obj) => {
                                                if (obj.userData?.type === 'floorTransitionArrow') {
                                                    const arrowPos = obj.position;
                                                    const dx = arrowPos.x - oldLastX;
                                                    const dy = arrowPos.y - oldLastY;
                                                    if (Math.sqrt(dx*dx + dy*dy) < 5) {
                                                        obj.position.set(position.x, position.y, obj.position.z);
                                                    }
                                                }
                                            });
                                        }
                                    }
                                }
                            }

                            shaftData.exitCount = occupiedCount;
                        }

                        // Kablo bacasından çıktı - ok'u kaldır
                        isInsideCableShaftRef.current = false;
                        currentCableShaftRef.current = null;
                        selectedExitPointRef.current = null;

                        // ✅ Snap marker temizle
                        if (exitPointSnapMarkerRef.current) {
                            scene.remove(exitPointSnapMarkerRef.current);
                            exitPointSnapMarkerRef.current.geometry?.dispose();
                            exitPointSnapMarkerRef.current.material?.dispose();
                            exitPointSnapMarkerRef.current = null;
                        }

                        // Ok preview'ı kaldır
                        if (arrowPreviewRef.current) {
                            scene.remove(arrowPreviewRef.current);
                            arrowPreviewRef.current.traverse(child => {
                                if (child.geometry) child.geometry.dispose();
                                if (child.material) child.material.dispose();
                            });
                            arrowPreviewRef.current = null;
                        }
                        arrowPreviewActiveRef.current = false;
                    }

                    // Component preview'ı göster
                    if (base.previewSymbolRef.current) {
                        base.previewSymbolRef.current.visible = true;
                    }
                }
            }

            if (base.snapWall) {
                // ✅ Midpoint snap aktif değilse kesikli kılavuz çizgilerini göster
                if (!midpointSnap) {
                    handleSnapWall({
                        scene,
                        camera,
                        mouseNDC,
                        worldPoint,
                        refs: {
                            intersectedLineRef: base.intersectedLineRef,
                            offsetLineRef: base.offsetLineRef,
                            verticesRef: base.verticesRef
                        },
                        floorPlans: base.floorPlans,
                        wires: base.wires,
                    });
                } else {
                    // Midpoint snap aktifken offset çizgilerini temizle
                    if (base.offsetLineRef.current) {
                        const arr = Array.isArray(base.offsetLineRef.current)
                            ? base.offsetLineRef.current
                            : [base.offsetLineRef.current];
                        arr.forEach(line => {
                            scene.remove(line);
                            line.geometry?.dispose?.();
                            line.material?.dispose?.();
                        });
                        base.offsetLineRef.current = null;
                    }
                }
            }

            // ✅ Ok ön izlemesi aktifse pozisyonunu güncelle (mouse ile hareket)
            if (arrowPreviewActiveRef.current && arrowPreviewRef.current && previewPoint) {
                arrowPreviewRef.current.position.set(previewPoint.x, previewPoint.y, 0);
            }
        };

        const handleKeyDown = (e) => {
            // ✅ enabled() kontrolünü kaldır - event listener zaten doğru commandType'da register edildi
            // if (!base.enabled()) return;
            // H5-2: AWSD klavye snap-offset hareket useWireKeyboardMovement'a çıkarıldı

            if (e.key === 't') {
                base.itemTypeIndexRef.current = (base.itemTypeIndexRef.current + 1) % base.itemTypes.length;
                base.itemTypeRef.current = base.itemTypes[base.itemTypeIndexRef.current];
                const typeLabel = options.itemTypeName === 'lightSwitch' ? 'Anahtar tipi' : 'Priz tipi';
                base.dispatch(setCommandMessage(`${typeLabel}: ${base.itemTypeRef.current.name}`));

                // If there's an active preview symbol, update it immediately so user sees the type change
                try {
                    if (base.previewSymbolRef.current) {
                        { const _ps = base.previewSymbolRef.current; if (_ps.parent) _ps.parent.remove(_ps); else scene.remove(_ps); }
                        base.previewSymbolRef.current.traverse(child => {
                            if (child.geometry) child.geometry.dispose();
                            if (child.material) child.material.dispose();
                        });
                        base.previewSymbolRef.current = null;
                    }

                    if (base.verticesRef.current.length > 0 && base.previewEndPointRef.current) {
                        const lastPoint = base.verticesRef.current[base.verticesRef.current.length - 1];
                        const directionVector = new THREE.Vector3().subVectors(base.previewEndPointRef.current, lastPoint).normalize();
                        const angle = Math.atan2(directionVector.y, directionVector.x);
                        const symbolPreview = base.createSymbol(base.previewEndPointRef.current, directionVector, angle, true, base.itemTypeRef.current, { rotateValue: rotateOffsetRef.current });
                        scene.add(symbolPreview);
                        base.previewSymbolRef.current = symbolPreview;
                    }
                } catch (err) {
                    // Failed to update preview type
                }

                return;
            }

            if (e.key === 'q') {
                const previousPos = base.verticesRef.current[base.verticesRef.current.length - 1];
                const direction = new THREE.Vector3(
                    base.previewEndPointRef.current.x - previousPos.x,
                    base.previewEndPointRef.current.y - previousPos.y,
                    0
                ).normalize();

                let newPos = new THREE.Vector3(
                    previousPos.x + direction.x * base.movementOffset,
                    previousPos.y + direction.y * base.movementOffset,
                    0
                );

                base.pushOrUpdateVertex(newPos);
                base.updateTempLine();
            }

            if (e.key === 'e') {
                const previousPos = base.verticesRef.current[base.verticesRef.current.length - 1];
                if (!previousPos) return;

                const mouseWorld = new THREE.Vector3(base.lastMouse.current.x, base.lastMouse.current.y, 0).unproject(camera);
                const deltaX = mouseWorld.x - previousPos.x;
                const deltaY = mouseWorld.y - previousPos.y;

                const offsetLength = 7.07106781187;
                const dx = offsetLength / Math.sqrt(2);
                const dy = dx;

                let offset = new THREE.Vector3();

                if (deltaX >= 0 && deltaY >= 0) {
                    offset.set(dx, dy, 0);
                } else if (deltaX < 0 && deltaY >= 0) {
                    offset.set(-dx, dy, 0);
                } else if (deltaX < 0 && deltaY < 0) {
                    offset.set(-dx, -dy, 0);
                } else {
                    offset.set(dx, -dy, 0);
                }

                const newPos = new THREE.Vector3().addVectors(previousPos, offset);
                base.pushOrUpdateVertex(newPos);
                base.updateTempLine();
            }

            if (e.key === 'Escape') {
                // ✅ ContinueFromShaft modunu temizle
                if (continueFromShaftModeRef.current) {
                    continueFromShaftModeRef.current = false;
                    continueFromShaftDataRef.current = null;

                    // Snap marker'ı gizle
                    if (exitPointSnapMarkerRef.current) {
                        exitPointSnapMarkerRef.current.visible = false;
                    }

                    // Çıkış noktalarını geri al (exitCount'u azalt)
                    if (currentCableShaftRef.current) {
                        const shaftGroup = currentCableShaftRef.current;
                        const shaftData = shaftGroup.userData;

                        // Mevcut çıkış noktalarını ve wire bilgilerini topla
                        const occupiedExitInfo = [];
                        shaftGroup.traverse((child) => {
                            if (child.userData?.type === 'shaftExitPoint' && child.userData?.isOccupied && child.userData?.wireId) {
                                occupiedExitInfo.push({
                                    wireId: child.userData.wireId,
                                    direction: child.userData.direction,
                                    position: child.position.clone()
                                });
                            }
                        });

                        // Mevcut çıkış noktalarını temizle
                        const toRemove = [];
                        shaftGroup.traverse((child) => {
                            if (child.userData?.type === 'shaftExitPoint') {
                                toRemove.push(child);
                            }
                        });
                        toRemove.forEach(child => {
                            shaftGroup.remove(child);
                            if (child.geometry) child.geometry.dispose();
                            if (child.material) child.material.dispose();
                        });

                        // Eski sayıda çıkış noktası oluştur (yeni eklenen dahil değil)
                        const occupiedCount = occupiedExitInfo.length;
                        if (occupiedCount > 0 && shaftData.diagonalStart && shaftData.diagonalEnd) {
                            for (let i = 0; i < occupiedCount; i++) {
                                const t = (i + 1) / (occupiedCount + 1);
                                const position = getPointOnDiagonal(shaftData.diagonalStart, shaftData.diagonalEnd, t);
                                const wireInfo = occupiedExitInfo[i];

                                const circleGeometry = new THREE.CircleGeometry(2, 16);
                                const circleMaterial = new THREE.MeshBasicMaterial({
                                    color: 0xff6600,
                                    side: THREE.DoubleSide,
                                    transparent: true,
                                    opacity: 0.8
                                });

                                const circle = new THREE.Mesh(circleGeometry, circleMaterial);
                                circle.position.copy(position);
                                circle.name = `shaft-exit-point-${i}`;
                                circle.userData = {
                                    type: 'shaftExitPoint',
                                    shaftId: shaftData.id,
                                    exitIndex: i,
                                    isExitPoint: true,
                                    isOccupied: true,
                                    wireId: wireInfo.wireId,
                                    direction: wireInfo.direction,
                                    isShaftPart: true
                                };

                                shaftGroup.add(circle);

                                // Wire vertex'lerini geri al
                                let oldLastX = null, oldLastY = null;
                                scene.traverse((obj) => {
                                    if (obj.userData?.id === wireInfo.wireId && obj.geometry?.attributes?.position) {
                                        const posAttr = obj.geometry.attributes.position;
                                        const numVerts = posAttr.count;
                                        if (numVerts > 0) {
                                            oldLastX = posAttr.getX(numVerts - 1);
                                            oldLastY = posAttr.getY(numVerts - 1);
                                        }
                                    }
                                });

                                scene.traverse((obj) => {
                                    if (obj.userData?.id === wireInfo.wireId && obj.geometry?.attributes?.position) {
                                        const posAttr = obj.geometry.attributes.position;
                                        const numVerts = posAttr.count;

                                        if (numVerts >= 2) {
                                            const cIdx = numVerts - 1;
                                            const bIdx = numVerts - 2;
                                            const cX = position.x;
                                            const cY = position.y;
                                            const bOldX = posAttr.getX(bIdx);
                                            const bOldY = posAttr.getY(bIdx);

                                            let newBx, newBy;

                                            if (numVerts >= 3) {
                                                const aIdx = numVerts - 3;
                                                const aX = posAttr.getX(aIdx);
                                                const aY = posAttr.getY(aIdx);
                                                const kDirX = bOldX - aX;
                                                const kDirY = bOldY - aY;

                                                if (Math.abs(kDirX) > Math.abs(kDirY)) {
                                                    newBx = cX;
                                                    newBy = bOldY;
                                                } else {
                                                    newBx = bOldX;
                                                    newBy = cY;
                                                }
                                            } else {
                                                const dirX = cX - bOldX;
                                                const dirY = cY - bOldY;

                                                if (Math.abs(dirX) > Math.abs(dirY)) {
                                                    newBx = cX;
                                                    newBy = bOldY;
                                                } else {
                                                    newBx = bOldX;
                                                    newBy = cY;
                                                }
                                            }

                                            posAttr.setXYZ(bIdx, newBx, newBy, 0);
                                            posAttr.setXYZ(cIdx, cX, cY, 0);
                                            posAttr.needsUpdate = true;
                                            obj.geometry.computeBoundingSphere();
                                        }
                                    }
                                });

                                // Arrow'ları güncelle
                                if (oldLastX !== null && oldLastY !== null) {
                                    scene.traverse((obj) => {
                                        if (obj.userData?.type === 'floorTransitionArrow') {
                                            const arrowPos = obj.position;
                                            const dx = arrowPos.x - oldLastX;
                                            const dy = arrowPos.y - oldLastY;
                                            if (Math.sqrt(dx*dx + dy*dy) < 5) {
                                                obj.position.set(position.x, position.y, obj.position.z);
                                            }
                                        }
                                    });
                                }
                            }

                            shaftData.exitCount = occupiedCount;
                            shaftData.occupiedExitCount = occupiedCount;
                        }

                        currentCableShaftRef.current = null;
                        isInsideCableShaftRef.current = false;
                        selectedExitPointRef.current = null;
                    }

                }

                // ✅ Ok ön izlemesini temizle
                if (arrowPreviewRef.current) {
                    scene.remove(arrowPreviewRef.current);
                    arrowPreviewRef.current.traverse(child => {
                        if (child.geometry) child.geometry.dispose();
                        if (child.material) child.material.dispose();
                    });
                    arrowPreviewRef.current = null;
                    arrowPreviewActiveRef.current = false;
                }
                base.cancelWiring();
            }

            if (e.key === ' ') {
                // ✅ Auto-finish devam ediyorsa işlem yapma
                if (base.autoFinishInProgressRef.current) return;

                // ✅ allowSpaceKey kontrolü - machine için Space tuşu devre dışı
                const allowSpaceKey = options.allowSpaceKey !== undefined ? options.allowSpaceKey : true;
                if (allowSpaceKey) {
                    // ✅ Event'in diğer handler'lara ulaşmasını engelle
                    e.preventDefault();
                    e.stopPropagation();

                    // Trace follow: önce trace noktalarını vertex olarak commit et
                    // Duplicate-consecutive guard: trace ilk noktası (last vertex'in
                    // guide projeksiyonu) bazen last vertex ile çakışıyor → atla.
                    if (tracePreviewPtsRef.current.length > 0 && base.startRef.current) {
                        for (const pt of tracePreviewPtsRef.current) {
                            const cloned = pt.clone();
                            const last = base.verticesRef.current[base.verticesRef.current.length - 1];
                            if (last && Math.abs(last.x - cloned.x) < 0.01 && Math.abs(last.y - cloned.y) < 0.01) continue;
                            const vcb = base.verticesRef.current.length;
                            base.verticesRef.current.push(cloned);
                            base.undoStackRef.current.push({ type: 'vertex', vertex: cloned, vertexCountBefore: vcb });
                        }
                        base.redoStackRef.current = [];
                        tracePreviewPtsRef.current = [];
                        traceGuideSideRef.current = 0;
                        traceGuideRef.current = null;
                    }

                    base.addItem(rotateOffsetRef.current);
                }
            }

            if (e.key === 'Enter') {
                // ✅ Auto-finish devam ediyorsa işlem yapma
                if (base.autoFinishInProgressRef.current) return;

                // ✅ Event'in diğer handler'lara ulaşmasını engelle
                e.preventDefault();
                e.stopPropagation();

                // Trace follow: önce trace noktalarını vertex olarak commit et
                // (duplicate-consecutive guard ile)
                if (tracePreviewPtsRef.current.length > 0 && base.startRef.current) {
                    for (const pt of tracePreviewPtsRef.current) {
                        const cloned = pt.clone();
                        const last = base.verticesRef.current[base.verticesRef.current.length - 1];
                        if (last && Math.abs(last.x - cloned.x) < 0.01 && Math.abs(last.y - cloned.y) < 0.01) continue;
                        const vcb = base.verticesRef.current.length;
                        base.verticesRef.current.push(cloned);
                        base.undoStackRef.current.push({ type: 'vertex', vertex: cloned, vertexCountBefore: vcb });
                    }
                    base.redoStackRef.current = [];
                    tracePreviewPtsRef.current = [];
                    traceGuideSideRef.current = 0;
                    traceGuideRef.current = null;
                }

                // ✅ confirmPlacement'ı çağır
                confirmPlacement();
            }

            // 'r' tuşu: H5'te useWireRotateOffset hook'una çıkarıldı (kendi event listener'i var)

            // ✅ Ok tuşlarına basıldığında ok yönü değiştirme (SADECE KABLO BACASI İÇİNDE)
            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                e.preventDefault();
                e.stopPropagation();

                // Kat-geçiş oku: kablo bacası ŞARTI KALDIRILDI (floorTransition extract) —
                // herhangi bir wire çizilirken üst/alt kata gidiş oku konabilir.

                // Hat çizimi başlamış olmalı ve preview noktası olmalı
                if (base.verticesRef.current.length < 1 || !base.previewEndPointRef.current) {
                    base.dispatch(setCommandMessage('Ok ön izleme için önce hat çizmeye başlayın'));
                    return;
                }

                // Ok yönü/rotasyonu + preview'ı shared helper ile oluştur (floorTransition modülü).
                const arrowRotation = FLOOR_ARROW_ROTATION[e.key] ?? 60;
                updateFloorArrowPreview({
                    scene,
                    position: base.previewEndPointRef.current,
                    rotation: arrowRotation,
                    wireColor: base.wireColor,
                    arrowRefs: {
                        previewRef: arrowPreviewRef,
                        activeRef: arrowPreviewActiveRef,
                        rotationRef: arrowPreviewRotationRef,
                    },
                });

                const directionText = e.key === 'ArrowUp' ? 'Yukarı kat' :
                                     e.key === 'ArrowDown' ? 'Aşağı kat' :
                                     e.key === 'ArrowLeft' ? 'Sol' : 'Sağ';
                base.dispatch(setCommandMessage(`Kat geçiş oku: ${directionText}`));
                return;
            }
        };

        domElement.addEventListener('contextmenu', (e) => e.preventDefault());
        domElement.addEventListener('mousedown', handleClick);
        domElement.addEventListener('mousemove', handleMouseMove);
        // ✅ keydown + capture mode kullan (useGlobalUndoRedo'dan önce yakala)
        window.addEventListener('keydown', handleKeyDown, { capture: true });
        
        // ✅ CTRL tuşu takibi için event listener'lar
        const handleCtrlDown = (e) => {
            if (e.key === 'Control') {
                base.ctrlPressedRef.current = true;
            }
        };
        const handleCtrlUp = (e) => {
            if (e.key === 'Control') {
                base.ctrlPressedRef.current = false;
            }
        };
        const handleShiftDown = (e) => {
            if (e.key === 'Shift') shiftPressedRef.current = true;
        };
        const handleShiftUp = (e) => {
            if (e.key === 'Shift') {
                shiftPressedRef.current = false;
                traceGuideSideRef.current = 0;
                traceGuideRef.current = null;
                tracePreviewPtsRef.current = [];
            }
        };
        window.addEventListener('keydown', handleCtrlDown);
        window.addEventListener('keyup', handleCtrlUp);
        window.addEventListener('keydown', handleShiftDown);
        window.addEventListener('keyup', handleShiftUp);

        return () => {
            domElement.removeEventListener('contextmenu', preventContextMenu);
            domElement.removeEventListener('mousedown', handleClick);
            domElement.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('keydown', handleKeyDown, { capture: true });
            window.removeEventListener('keydown', handleCtrlDown);
            window.removeEventListener('keyup', handleCtrlUp);
            window.removeEventListener('keydown', handleShiftDown);
            window.removeEventListener('keyup', handleShiftUp);
            tracePreviewPtsRef.current = [];
        };
    }, [base.commandType, scene, camera, renderer, base.gridMode, base.objectSnap, base.flipDirection, snapPoints, base.dispatch, base.onWireEnd, base.operationData?.continueFromShaft]);


};

export default useComponentWiring;
